import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BookUser, RefreshCw, Download, Upload, Globe, Search, Loader2, Mail, Phone, Building2, ShieldAlert, Check, X,
  Trash2, Pencil, ExternalLink, UserPlus, Landmark, Sparkles, Info, CalendarDays, Ticket, Send, AlertTriangle, History, RotateCcw, Archive, BellRing, ListChecks,
  CalendarClock, Clock, CheckCircle2, XCircle, Ban, Users, ChevronDown, ChevronUp, Zap, Eye, MessageSquareReply, Inbox, Copy, Radio, Settings2, PenLine, Plus, MapPin,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { PostalCodeInput } from "../components/PostalCodeInput";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { api, errorMessage, formatDateTime } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_STYLES = {
  ikke_kontaktet: "bg-secondary text-secondary-foreground border-border",
  kontaktet: "bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border-[hsl(var(--info))]/30",
  interessert: "bg-[hsl(var(--warning))]/20 text-[hsl(var(--warning-foreground))] border-[hsl(var(--warning))]/40",
  registrert: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  ikke_aktuell: "bg-muted text-muted-foreground border-border line-through",
};
const KIND_STYLES = {
  forening: "bg-brand-soft text-brand border-brand/25",
  stiftelse: "bg-cat-foredrag-soft text-cat-foredrag border-cat-foredrag/25",
  kommunal: "bg-cat-festival-soft text-cat-festival border-cat-festival/25",
  kulturhus: "bg-cat-teater-soft text-cat-teater border-cat-teater/25",
  servering: "bg-cat-sport-soft text-cat-sport border-cat-sport/25",
  kirke: "bg-info/10 text-info border-info/25",
  annet: "bg-cat-annet-soft text-cat-annet border-cat-annet/25",
};
const SOURCE_LABELS = { brreg_role: "Rolle (Brønnøysund)", manual: "Lagt inn manuelt", import: "Importert", website: "Fra nettside" };
const CAMPAIGN_STATUS_STYLES = {
  scheduled: "bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border-[hsl(var(--info))]/30",
  sending: "bg-[hsl(var(--warning))]/20 text-[hsl(var(--warning-foreground))] border-[hsl(var(--warning))]/40",
  sent: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30",
  cancelled: "bg-muted text-muted-foreground border-border",
  failed: "bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30",
};
const CAMPAIGN_STATUS_ICONS = { scheduled: Clock, sending: Loader2, sent: CheckCircle2, cancelled: Ban, failed: XCircle };
const EMAIL_LANG_LABELS = { nn: "Nynorsk", nb: "Bokmål" };

// Planlagte utsendinger velges i norsk tid (Europe/Oslo) og lagres i UTC på serveren.
const OSLO_TZ = "Europe/Oslo";
const tzOffsetMinutes = (date, tz) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(date);
  const p = Object.fromEntries(parts.filter((x) => x.type !== "literal").map((x) => [x.type, parseInt(x.value, 10)]));
  return Math.round((Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - date.getTime()) / 60000);
};
/** «2026-09-26» + «09:00» (norsk veggklokke) -> ISO 8601 i UTC. Håndterer sommer-/vintertid. */
const osloToIso = (dateStr, timeStr) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr || "") || !/^\d{2}:\d{2}$/.test(timeStr || "")) return null;
  const naive = new Date(`${dateStr}T${timeStr}:00Z`);
  if (Number.isNaN(naive.getTime())) return null;
  const off = tzOffsetMinutes(naive, OSLO_TZ);
  let utc = new Date(naive.getTime() - off * 60000);
  const off2 = tzOffsetMinutes(utc, OSLO_TZ);
  if (off2 !== off) utc = new Date(naive.getTime() - off2 * 60000);
  return utc.toISOString();
};
/** Dato i Oslo som «YYYY-MM-DD» (sv-SE gir ISO-format). */
const osloDateStr = (date) => new Intl.DateTimeFormat("sv-SE", { timeZone: OSLO_TZ }).format(date);
/** ISO -> «26.09.2026 kl. 09:00» i norsk tid. */
const fmtOslo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const parts = new Intl.DateTimeFormat("nb-NO", { timeZone: OSLO_TZ, hourCycle: "h23", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }).formatToParts(d);
  const p = Object.fromEntries(parts.filter((x) => x.type !== "literal").map((x) => [x.type, x.value]));
  return `${p.day}.${p.month}.${p.year} kl. ${p.hour}:${p.minute}`;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return d && m && y ? `${d}.${m}.${y}` : iso;
};

const downloadBlob = (data, filename) => {
  const url = window.URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const slugify = (txt) => (txt || "").toLowerCase().replace(/å/g, "a").replace(/ø/g, "o").replace(/æ/g, "ae").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function AdminProspectsPage() {
  const { organizer } = useAuth();
  const [meta, setMeta] = useState(null);
  const [kommune, setKommune] = useState("4617");
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("alle");
  const [status, setStatus] = useState("alle");
  const [hasEmail, setHasEmail] = useState(false);
  const [frivillig, setFrivillig] = useState(false);
  const [missing, setMissing] = useState(false);
  const [rightin, setRightin] = useState("alle");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(60);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sync, setSync] = useState(null);
  const [starting, setStarting] = useState(false);
  const [exporting, setExporting] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [toDelete, setToDelete] = useState(null); // org som skal slettes permanent (bekreftelse)
  const [deleting, setDeleting] = useState(false);
  const [excludedOpen, setExcludedOpen] = useState(false);
  const [subscribersOpen, setSubscribersOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false); // redigeringsmodus: kryss av flere for sletting/utsending
  const [checked, setChecked] = useState(() => new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [campaignOpen, setCampaignOpen] = useState(false); // planlegg masseutsending til avkryssede
  const [areaTarget, setAreaTarget] = useState(null); // områdeutsending: heile kommunen/regionen (med gjeldande filter) som målgruppe
  const [campaignsOpen, setCampaignsOpen] = useState(false); // liste over utsendinger
  const [redo, setRedo] = useState(null); // «Lag på nytt»: {source, orgs, excluded, excluded_summary} fra en tidligere utsending
  const [settingsOpen, setSettingsOpen] = useState(false); // signaturnavn (eget + felles)
  const [newOrgOpen, setNewOrgOpen] = useState(false); // legg inn organisasjon manuelt
  const fileRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  // Dyplenke fra dashbordets helsekort: /dashboard/kontaktregister-admin?oppsett=1 åpner Utsendinger med oppsettet utvidet
  const openSetupFromLink = useMemo(() => new URLSearchParams(location.search).get("oppsett") === "1", [location.search]);
  useEffect(() => {
    if (openSetupFromLink) setCampaignsOpen(true);
  }, [openSetupFromLink]);
  const closeCampaigns = () => {
    setCampaignsOpen(false);
    if (openSetupFromLink) navigate("/dashboard/kontaktregister-admin", { replace: true });
  };

  const regionList = useMemo(() => {
    if (Array.isArray(meta?.regioner) && meta.regioner.length && typeof meta.regioner[0] === "object") return meta.regioner;
    // Fallback (eldre backend): bygg regionane frå kommunelista
    const names = [...new Set((meta?.kommuner || []).map((k) => k.region || "Andre"))];
    return names.map((navn) => {
      const ks = (meta?.kommuner || []).filter((k) => (k.region || "Andre") === navn);
      return { navn, value: `region:${navn}`, total: ks.reduce((a, k) => a + (k.total || 0), 0), with_email: ks.reduce((a, k) => a + (k.with_email || 0), 0), kommuner: ks.map((k) => k.nummer) };
    });
  }, [meta]);

  const filterParams = useMemo(() => ({
    kommune,
    q: q.trim() || undefined,
    kind: kind !== "alle" ? kind : undefined,
    status: status !== "alle" ? status : undefined,
    has_email: hasEmail ? "ja" : undefined,
    frivillig: frivillig ? "ja" : undefined,
    missing: missing ? "ja" : undefined,
    rightin: rightin !== "alle" ? rightin : undefined,
  }), [kommune, q, kind, status, hasEmail, frivillig, missing, rightin]);

  const loadMeta = useCallback(() => {
    api.get("/admin/prospects/meta").then((res) => {
      setMeta(res.data);
      setSync(res.data.sync);
    }).catch((err) => toast.error(errorMessage(err)));
  }, []);

  const loadList = useCallback(() => {
    setLoading(true);
    api.get("/admin/prospects", { params: filterParams })
      .then((res) => {
        setItems(res.data.items);
        setVisible(60);
        setChecked(new Set());
      })
      .catch((err) => toast.error(errorMessage(err)))
      .finally(() => setLoading(false));
  }, [filterParams]);

  useEffect(() => {
    if (!organizer?.is_admin) return;
    loadMeta();
  }, [organizer?.is_admin, loadMeta]);

  // Dyplenke fra videresendte svar: …/kontaktregister-admin?org=<id> åpner organisasjonen direkte
  const deepLinkDone = useRef(false);
  useEffect(() => {
    if (!organizer?.is_admin || deepLinkDone.current) return;
    const id = new URLSearchParams(window.location.search).get("org");
    if (!id) return;
    deepLinkDone.current = true;
    setDetailLoading(true);
    setSelected({ id, name: "", contacts: [] });
    api.get(`/admin/prospects/${id}`)
      .then((res) => { setSelected(res.data); if (res.data.kommunenummer) setKommune(res.data.kommunenummer); })
      .catch((err) => { toast.error(errorMessage(err)); setSelected(null); })
      .finally(() => setDetailLoading(false));
  }, [organizer?.is_admin]);

  useEffect(() => {
    if (!organizer?.is_admin) return;
    const t = setTimeout(loadList, 250);
    return () => clearTimeout(t);
  }, [organizer?.is_admin, loadList]);

  // Poll synk-status mens den kjører; last listen på nytt når den er ferdig
  useEffect(() => {
    if (sync?.status !== "running") return;
    const t = setInterval(() => {
      api.get("/admin/prospects/sync-status").then((res) => {
        setSync(res.data);
        if (res.data.status !== "running") {
          toast.success("Kontaktregisteret er oppdatert fra Brønnøysund");
          loadMeta();
          loadList();
        }
      }).catch(() => {});
    }, 4000);
    return () => clearInterval(t);
  }, [sync?.status, loadMeta, loadList]);

  const startSync = async () => {
    setStarting(true);
    try {
      const res = await api.post("/admin/prospects/sync", {});
      setSync(res.data.state);
      toast.info("Synkronisering startet – dette tar noen minutter og kjører i bakgrunnen");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setStarting(false);
    }
  };

  const openDetail = async (org) => {
    setDetailLoading(true);
    setSelected({ ...org, contacts: [] });
    try {
      const res = await api.get(`/admin/prospects/${org.id}`);
      setSelected(res.data);
    } catch (err) {
      toast.error(errorMessage(err));
      setSelected(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const applyOrgUpdate = (updated) => {
    setItems((prev) => prev.map((o) => (o.id === updated.id ? { ...o, ...updated } : o)));
    setSelected((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
  };

  const exportCsv = async (mode) => {
    setExporting(mode);
    try {
      const res = await api.get("/admin/prospects/export.csv", { params: { ...filterParams, mode }, responseType: "blob" });
      const kommuneNavn = kommune.startsWith("region:") ? kommune.slice(7) : (meta?.kommuner.find((k) => k.nummer === kommune)?.navn || "vestland");
      downloadBlob(res.data, `kontaktregister-${mode === "contacts" ? "kontakter" : "organisasjoner"}-${kommuneNavn.toLowerCase()}.csv`);
      toast.success("CSV lastet ned");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setExporting("");
    }
  };

  const handleImport = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/admin/prospects/import", form, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(res.data);
      toast.success(`${res.data.imported} nye og ${res.data.updated} oppdaterte kontakter`);
      loadList();
      loadMeta();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      const res = await api.delete(`/admin/prospects/${toDelete.id}`);
      setItems((prev) => prev.filter((o) => o.id !== toDelete.id));
      setSelected((prev) => (prev && prev.id === toDelete.id ? null : prev));
      toast.success(`«${res.data.name}» er slettet permanent${res.data.deleted_contacts ? ` sammen med ${res.data.deleted_contacts} person${res.data.deleted_contacts === 1 ? "" : "er"}` : ""}`);
      setToDelete(null);
      loadMeta();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  const toggleChecked = (id) => setChecked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const shownItems = items.slice(0, visible);
  const allShownChecked = shownItems.length > 0 && shownItems.every((o) => checked.has(o.id));
  const toggleAllShown = () => setChecked((prev) => {
    const next = new Set(prev);
    if (allShownChecked) shownItems.forEach((o) => next.delete(o.id)); else shownItems.forEach((o) => next.add(o.id));
    return next;
  });
  const checkedItems = items.filter((o) => checked.has(o.id));
  const checkedContacts = checkedItems.reduce((sum, o) => sum + (o.contacts_active || 0) + (o.contacts_suggested || 0), 0);
  const exitSelectMode = () => { setSelectMode(false); setChecked(new Set()); };

  const confirmBulkDelete = async () => {
    if (checkedItems.length === 0) return;
    setDeleting(true);
    try {
      const res = await api.post("/admin/prospects/bulk-delete", { ids: checkedItems.map((o) => o.id) });
      const ids = new Set(checkedItems.map((o) => o.id));
      setItems((prev) => prev.filter((o) => !ids.has(o.id)));
      setSelected((prev) => (prev && ids.has(prev.id) ? null : prev));
      toast.success(`${res.data.deleted} organisasjon${res.data.deleted === 1 ? "" : "er"} slettet permanent${res.data.deleted_contacts ? ` sammen med ${res.data.deleted_contacts} person${res.data.deleted_contacts === 1 ? "" : "er"}` : ""}`);
      setBulkConfirm(false);
      exitSelectMode();
      loadMeta();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  const onRestored = (org) => {
    loadMeta();
    if (org && inCurrentArea(org)) loadList();
  };

  if (organizer && !organizer.is_admin) {
    return (
      <DashboardLayout>
        <p className="text-muted-foreground" data-testid="admin-prospects-denied">Denne siden krever plattform-administrator.</p>
      </DashboardLayout>
    );
  }

  const currentKommune = meta?.kommuner.find((k) => k.nummer === kommune);
  const currentRegion = kommune.startsWith("region:") ? regionList.find((r) => r.value === kommune) : null;
  // Høyrer organisasjonen til det valde området (kommune, region eller alle)?
  const inCurrentArea = (org) => kommune === "alle" || org?.kommunenummer === kommune || (currentRegion && (currentRegion.kommuner || []).includes(org?.kommunenummer));
  const running = sync?.status === "running";

  return (
    <DashboardLayout>
      <div className="max-w-6xl space-y-6" data-testid="admin-prospects-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight flex items-center gap-2">
            <BookUser className="w-7 h-7 text-brand" /> Kontaktregister (admin)
          </h1>
          <p className="text-muted-foreground mt-1">
            Målrettet mot dem som arrangerer: kulturlag (korps, kor, teater, festival, historielag), kulturhus, kino og scener, restauranter, barer og hoteller, kirker, menigheter og bedehus, museer og kommunale kulturenheter i hele Vestland fylke (43 kommuner) – hentet fra Brønnøysundregistrene, med oppfølging og kontaktpersoner. Velforeninger, idrettslag, FAU o.l. tas ikke med.
          </p>
        </div>

        <Alert className="border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10" data-testid="admin-prospects-gdpr-notice">
          <ShieldAlert className="h-4 w-4 text-[hsl(var(--warning-foreground))]" />
          <AlertTitle>Personopplysninger – Kviteberg Teknologi er ansvarlig</AlertTitle>
          <AlertDescription className="text-sm">
            Registeret inneholder offentlige roller fra Brønnøysund, kontakter du legger inn eller importerer, og <strong>forslag</strong> hentet fra organisasjonenes egne nettsider.
            Forslag er inaktive til du godkjenner dem. Alle personer kan slettes med ett klikk. Bruk i samsvar med GDPR og markedsføringsloven § 15 er Kviteberg Teknologis ansvar.
          </AlertDescription>
        </Alert>

        {/* Synk-kort */}
        <Card className="border-border" data-testid="admin-prospects-sync-card">
          <CardContent className="p-5 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 space-y-1 min-w-0">
              <div className="font-heading font-semibold flex items-center gap-2">
                <Landmark className="w-4 h-4 text-brand" /> Brønnøysundregistrene
                {running && <Badge className="bg-[hsl(var(--info))] text-[hsl(var(--info-foreground))] hover:bg-[hsl(var(--info))]" data-testid="admin-prospects-sync-running">Synkroniserer …</Badge>}
              </div>
              {running ? (
                <p className="text-sm text-muted-foreground" data-testid="admin-prospects-sync-progress">
                  Fase: <strong className="text-foreground">{sync.phase}</strong> · {sync.done ?? 0} av {sync.total ?? 0}
                  {sync.counts && <> · {sync.counts.orgs} organisasjoner, {sync.counts.roles} roller, {sync.counts.suggestions} nettsideforslag</>}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="admin-prospects-sync-last">
                  {sync?.last_success_at ? <>Sist oppdatert {formatDateTime(sync.last_success_at)}</> : "Ikke synkronisert ennå – første synk starter automatisk."}
                  {" "}· Oppdateres automatisk hver uke.
                  {sync?.error && <span className="text-[hsl(var(--danger))]"> Siste feil: {sync.error}</span>}
                </p>
              )}
              {meta && (
                <p className="text-xs text-muted-foreground" data-testid="admin-prospects-totals">
                  {meta.totals.orgs} organisasjoner · {meta.totals.contacts_active} aktive kontakter · {meta.totals.suggestions} forslag til godkjenning · {meta.totals.rightin_organizers} er allerede Right in-arrangører
                  {meta.totals.newsletter_subscribers != null && <> · {meta.totals.newsletter_subscribers} nyhetsbrev-abonnenter</>}
                </p>
              )}
            </div>
            <Button onClick={startSync} disabled={running || starting} data-testid="admin-prospects-sync-button">
              {running || starting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
              Oppdater fra Brønnøysund
            </Button>
          </CardContent>
        </Card>

        {/* Kommunevelger – hele Vestland (43 kommuner) gruppert etter region */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3" data-testid="admin-prospects-kommune-picker">
          <Select value={kommune} onValueChange={setKommune}>
            <SelectTrigger className="bg-card sm:w-[320px] h-11" data-testid="admin-prospects-kommune-select">
              <MapPin className="w-4 h-4 mr-2 text-brand shrink-0" />
              <SelectValue placeholder="Velg kommune" />
            </SelectTrigger>
            <SelectContent className="max-h-[70vh]">
              <SelectItem value="alle" data-testid="admin-prospects-kommune-alle">
                Hele Vestland{meta?.totals?.orgs != null && <span className="ml-2 text-xs tabular-nums text-muted-foreground">{meta.totals.orgs}</span>}
              </SelectItem>
              {regionList.map((region) => {
                const list = (meta?.kommuner || [{ nummer: "4617", navn: "Kvinnherad", region: "Sunnhordland" }]).filter((k) => k.nummer !== "9999" && (k.region || "Andre") === region.navn);
                if (!list.length) return null;
                return (
                  <SelectGroup key={region.navn}>
                    <SelectLabel className="text-xs uppercase tracking-wide text-muted-foreground">{region.navn}</SelectLabel>
                    {/* Heile regionen er valbar – alle kommunane i regionen på ein gong */}
                    <SelectItem value={region.value} className="font-semibold" data-testid={`admin-prospects-region-${slugify(region.navn)}`}>
                      Hele {region.navn}
                      {region.total != null && <span className="ml-2 text-xs font-normal tabular-nums text-muted-foreground">{region.total}</span>}
                    </SelectItem>
                    {list.map((k) => (
                      <SelectItem key={k.nummer} value={k.nummer} className="pl-10" data-testid={`admin-prospects-kommune-${k.nummer}`}>
                        {k.navn}
                        {k.total != null && <span className="ml-2 text-xs tabular-nums text-muted-foreground">{k.total}</span>}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                );
              })}
            </SelectContent>
          </Select>
          {currentKommune && (
            <p className="text-sm text-muted-foreground" data-testid="admin-prospects-kommune-summary">
              {currentKommune.region ? `${currentKommune.region} · ` : ""}{currentKommune.total ?? 0} organisasjoner, {currentKommune.with_email ?? 0} med e-post
            </p>
          )}
          {currentRegion && (
            <p className="text-sm text-muted-foreground" data-testid="admin-prospects-kommune-summary">
              Hele {currentRegion.navn} ({currentRegion.kommuner?.length || 0} kommuner) · {currentRegion.total ?? 0} organisasjoner, {currentRegion.with_email ?? 0} med e-post
            </p>
          )}
          {kommune === "alle" && meta && (
            <p className="text-sm text-muted-foreground" data-testid="admin-prospects-kommune-summary">
              Alle 43 kommuner i Vestland · {meta.totals.orgs} organisasjoner
            </p>
          )}
        </div>

        {/* Filter */}
        <div className="rounded-2xl border border-border bg-card p-4 space-y-3" data-testid="admin-prospects-filter-bar">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk på navn, org.nr, e-post, poststed eller notat …" className="pl-9 bg-background" data-testid="admin-prospects-search" />
            </div>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="bg-background lg:w-[220px]" data-testid="admin-prospects-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle typer</SelectItem>
                {(meta?.kinds || []).map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="bg-background lg:w-[200px]" data-testid="admin-prospects-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle statuser</SelectItem>
                {(meta?.statuses || []).map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={rightin} onValueChange={setRightin}>
              <SelectTrigger className="bg-background lg:w-[200px]" data-testid="admin-prospects-rightin-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Med og uten Right in</SelectItem>
                <SelectItem value="nei">Ikke Right in-arrangør</SelectItem>
                <SelectItem value="ja">Er Right in-arrangør</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={hasEmail} onCheckedChange={setHasEmail} data-testid="admin-prospects-has-email-switch" /> Bare med e-post
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={frivillig} onCheckedChange={setFrivillig} data-testid="admin-prospects-frivillig-switch" /> Bare Frivillighetsregisteret
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={missing} onCheckedChange={setMissing} data-testid="admin-prospects-missing-switch" /> Bare uten kontaktinfo
            </label>
            <div className="ml-auto flex flex-wrap gap-2">
              <Button variant="secondary" size="sm" onClick={() => exportCsv("orgs")} disabled={!!exporting} data-testid="admin-prospects-export-orgs">
                {exporting === "orgs" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} Organisasjoner (CSV)
              </Button>
              <Button variant="secondary" size="sm" onClick={() => exportCsv("contacts")} disabled={!!exporting} data-testid="admin-prospects-export-contacts">
                {exporting === "contacts" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} Kontakter (CSV)
              </Button>
              <Button size="sm" onClick={() => setNewOrgOpen(true)} data-testid="admin-prospects-new-org-button">
                <Plus className="w-4 h-4 mr-1.5" /> Ny organisasjon
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setImportResult(null); setImportOpen(true); }} data-testid="admin-prospects-import-button">
                <Upload className="w-4 h-4 mr-1.5" /> Importer liste
              </Button>
              <Button variant="outline" size="sm" onClick={() => setSubscribersOpen(true)} data-testid="admin-prospects-subscribers-button">
                <BellRing className="w-4 h-4 mr-1.5" /> Nyhetsbrev{meta?.totals?.newsletter_subscribers ? ` (${meta.totals.newsletter_subscribers})` : ""}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCampaignsOpen(true)} data-testid="admin-prospects-campaigns-button">
                <CalendarClock className="w-4 h-4 mr-1.5" /> Utsendinger
                {(meta?.totals?.campaigns_scheduled || meta?.totals?.campaigns_sending) ? (
                  <Badge className="ml-1.5 h-5 px-1.5 text-[11px] bg-[hsl(var(--info))] text-[hsl(var(--info-foreground))] hover:bg-[hsl(var(--info))]" data-testid="admin-prospects-campaigns-pending-badge">
                    {(meta.totals.campaigns_scheduled || 0) + (meta.totals.campaigns_sending || 0)}
                  </Badge>
                ) : null}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} data-testid="admin-prospects-settings-button">
                <Settings2 className="w-4 h-4 mr-1.5" /> Innstillinger
              </Button>
              <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setExcludedOpen(true)} data-testid="admin-prospects-excluded-button">
                <Archive className="w-4 h-4 mr-1.5" /> Slettede{meta?.totals?.excluded ? ` (${meta.totals.excluded})` : ""}
              </Button>
            </div>
          </div>
        </div>

        {/* Liste */}
        {loading ? (
          <div className="space-y-2" data-testid="admin-prospects-loading">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
          </div>
        ) : items.length === 0 ? (
          <Card className="border-dashed border-border">
            <CardContent className="py-12 text-center text-muted-foreground" data-testid="admin-prospects-empty">
              {meta?.totals.orgs === 0
                ? "Registeret er tomt. Trykk «Oppdater fra Brønnøysund» for å hente organisasjonene."
                : "Ingen organisasjoner passer filteret. Mangler noen? Legg dem inn med «Ny organisasjon»."}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground" data-testid="admin-prospects-count">
                {items.length} organisasjon{items.length === 1 ? "" : "er"}{currentKommune ? ` i ${currentKommune.navn}` : ""}
              </p>
              {selectMode ? (
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={allShownChecked} onCheckedChange={toggleAllShown} data-testid="admin-prospects-select-all" /> Velg alle viste ({shownItems.length})
                  </label>
                  <Button variant="ghost" size="sm" onClick={exitSelectMode} data-testid="admin-prospects-select-cancel">Avbryt</Button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setSelectMode(true)} data-testid="admin-prospects-select-mode">
                  <ListChecks className="w-4 h-4 mr-1.5" /> Velg flere
                </Button>
                <Button
                  size="sm"
                  onClick={() => { setAreaTarget({ kommune, q: filterParams.q, kind: filterParams.kind, status: filterParams.status, has_email: filterParams.has_email, frivillig: filterParams.frivillig, rightin: filterParams.rightin }); setCampaignOpen(true); }}
                  disabled={!items || items.length === 0}
                  title="Planlegg én utsending til alle organisasjonene i valgt kommune/region (med filtrene som er satt)"
                  data-testid="admin-prospects-area-campaign"
                >
                  <Send className="w-4 h-4 mr-1.5" /> Send til {currentRegion ? `hele ${currentRegion.navn}` : (kommune === "alle" ? "hele Vestland" : `hele ${currentKommune?.navn || "kommunen"}`)}
                </Button>
                </div>
              )}
            </div>
            <div className="rounded-2xl border border-border bg-card divide-y divide-border overflow-hidden" data-testid="admin-prospects-list">
              {shownItems.map((o) => (
                <div
                  key={o.id}
                  className={`flex items-stretch transition-colors duration-150 ${selectMode && checked.has(o.id) ? "bg-brand-soft/60" : "hover:bg-secondary/60"}`}
                  data-testid={`admin-prospect-item-${o.org_number || o.id}`}
                  data-checked={selectMode && checked.has(o.id) ? "true" : "false"}
                >
                {selectMode && (
                  <div className="flex items-center pl-4">
                    <Checkbox checked={checked.has(o.id)} onCheckedChange={() => toggleChecked(o.id)} aria-label={`Velg ${o.name}`} data-testid={`admin-prospect-check-${o.org_number || o.id}`} />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => (selectMode ? toggleChecked(o.id) : openDetail(o))}
                  className={`min-w-0 flex-1 text-left ${selectMode ? "pl-3" : "pl-4"} pr-2 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 focus-visible:outline-none focus-visible:bg-secondary`}
                  data-testid={`admin-prospect-row-${o.org_number || o.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium truncate">{o.name}</span>
                      <Badge variant="outline" className={`text-[11px] ${KIND_STYLES[o.kind] || KIND_STYLES.annet}`}>{o.kind_label}</Badge>
                      {o.source === "manual" && (
                        <Badge variant="secondary" className="text-[11px]" title="Lagt inn manuelt av admin – ikke fra Brønnøysund" data-testid={`admin-prospect-manual-${o.org_number || o.id}`}>
                          <PenLine className="w-3 h-3 mr-1" /> Manuell
                        </Badge>
                      )}
                      {o.is_rightin_organizer && (
                        <Badge className="text-[11px] bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]" data-testid={`admin-prospect-rightin-${o.org_number || o.id}`}>
                          <Ticket className="w-3 h-3 mr-1" /> Right in
                        </Badge>
                      )}
                      {o.in_frivillighetsregisteret && <Badge variant="secondary" className="text-[11px]">Frivillig</Badge>}
                      {o.newsletter_subscribed && (
                        <Badge variant="outline" className="text-[11px] border-brand/30 bg-brand-soft text-brand" data-testid={`admin-prospect-newsletter-${o.org_number || o.id}`}>
                          <BellRing className="w-3 h-3 mr-1" /> Abonnent
                        </Badge>
                      )}
                      {o.replies_count > 0 && (
                        <Badge variant="outline" className="text-[11px] border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" data-testid={`admin-prospect-replies-${o.org_number || o.id}`}>
                          <MessageSquareReply className="w-3 h-3 mr-1" /> {o.replies_count} svar
                        </Badge>
                      )}
                      {o.kind === "kommunal" && o.missing_contact_info && (
                        <Badge variant="outline" className="text-[11px] border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning-foreground))]" data-testid={`admin-prospect-missing-${o.org_number || o.id}`}>
                          <AlertTriangle className="w-3 h-3 mr-1" /> Mangler kontaktinfo
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                      <span>{o.kommune}{o.address?.poststed ? ` · ${o.address.poststed}` : ""}</span>
                      {o.email ? (
                        <span className="flex items-center gap-1 text-foreground"><Mail className="w-3 h-3" />{o.email}</span>
                      ) : o.contacts_with_email > 0 ? (
                        <span className="flex items-center gap-1 text-foreground" data-testid={`admin-prospect-contact-email-${o.org_number || o.id}`}><Mail className="w-3 h-3" />E-post via {o.contacts_with_email === 1 ? "kontaktperson" : `${o.contacts_with_email} kontaktpersoner`}</span>
                      ) : (
                        <span className="italic">Ingen e-post i registeret</span>
                      )}
                      {o.website && <span className="flex items-center gap-1"><Globe className="w-3 h-3" />nettside</span>}
                      <span>{o.contacts_active} kontakt{o.contacts_active === 1 ? "" : "er"}{o.contacts_suggested ? ` · ${o.contacts_suggested} forslag` : ""}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 text-xs">
                    {o.last_email_sent_at && (
                      <span className="text-muted-foreground hidden md:inline" data-testid={`admin-prospect-lastmail-${o.org_number || o.id}`}>Siste e-post {fmtDate(o.last_email_sent_at)}</span>
                    )}
                    <Badge variant="outline" className={STATUS_STYLES[o.followup_status] || STATUS_STYLES.ikke_kontaktet} data-testid={`admin-prospect-status-${o.org_number || o.id}`}>
                      {o.followup_label}
                    </Badge>
                  </div>
                </button>
                {!selectMode && (
                <div className="flex items-center pr-2">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-9 w-9 text-muted-foreground hover:text-[hsl(var(--danger))]"
                    onClick={() => setToDelete(o)}
                    title="Slett organisasjonen permanent"
                    aria-label={`Slett ${o.name}`}
                    data-testid={`admin-prospect-delete-${o.org_number || o.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                )}
                </div>
              ))}
            </div>
            {visible < items.length && (
              <div className="text-center pt-2">
                <Button variant="secondary" onClick={() => setVisible((v) => v + 100)} data-testid="admin-prospects-show-more">
                  Vis flere ({items.length - visible} igjen)
                </Button>
              </div>
            )}
          </div>
        )}

        <ProspectDetailDialog
          org={selected}
          loading={detailLoading}
          statuses={meta?.statuses || []}
          kinds={meta?.kinds || []}
          kommuner={(meta?.kommuner || []).filter((k) => k.nummer !== "9999")}
          onClose={() => setSelected(null)}
          onDelete={(org) => setToDelete(org)}
          onOrgUpdate={applyOrgUpdate}
          onContactsChange={(contacts) => {
            setSelected((prev) => (prev ? { ...prev, contacts } : prev));
            const active = contacts.filter((c) => c.status === "active").length;
            const suggested = contacts.filter((c) => c.status === "suggested").length;
            const withEmail = contacts.filter((c) => c.status === "active" && c.email).length;
            const patch = (o) => ({ ...o, contacts_active: active, contacts_suggested: suggested, contacts_with_email: withEmail, missing_contact_info: !o.email && withEmail === 0 });
            setSelected((prev) => (prev ? patch(prev) : prev));
            setItems((prev) => prev.map((o) => (selected && o.id === selected.id ? patch(o) : o)));
          }}
        />

        {selectMode && (
          <div className="sticky bottom-4 z-30 flex justify-center pointer-events-none" data-testid="admin-prospects-bulk-bar">
            <div className="pointer-events-auto rounded-2xl border border-border bg-card/95 backdrop-blur-md shadow-lg px-4 py-3 flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium" data-testid="admin-prospects-bulk-count">
                {checkedItems.length} valgt{checkedContacts ? ` · ${checkedContacts} person${checkedContacts === 1 ? "" : "er"}` : ""}
              </span>
              <Button variant="ghost" size="sm" onClick={exitSelectMode} data-testid="admin-prospects-bulk-cancel">Avbryt</Button>
              <Button
                size="sm"
                disabled={checkedItems.length === 0}
                onClick={() => setCampaignOpen(true)}
                data-testid="admin-prospects-bulk-email"
              >
                <Send className="w-4 h-4 mr-1.5" /> Send e-post<span className="hidden sm:inline">&nbsp;til valgte</span>{checkedItems.length ? ` (${checkedItems.length})` : ""}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-[hsl(var(--danger))] border-[hsl(var(--danger))]/40 hover:bg-[hsl(var(--danger))]/10 hover:text-[hsl(var(--danger))]"
                disabled={checkedItems.length === 0}
                onClick={() => setBulkConfirm(true)}
                data-testid="admin-prospects-bulk-delete"
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> Slett<span className="hidden sm:inline">&nbsp;valgte</span>{checkedItems.length ? ` (${checkedItems.length})` : ""}
              </Button>
            </div>
          </div>
        )}

        <CampaignDialog
          open={campaignOpen}
          orgs={redo ? redo.orgs : (areaTarget ? [] : checkedItems)}
          redo={redo}
          target={areaTarget}
          onClose={() => { setCampaignOpen(false); setRedo(null); setAreaTarget(null); }}
          onCreated={(c) => {
            setCampaignOpen(false);
            setRedo(null);
            setAreaTarget(null);
            exitSelectMode();
            loadMeta();
            if (c?.status === "sent" || c?.status === "sending") loadList();
            setCampaignsOpen(true);
          }}
        />
        <CampaignsDialog
          open={campaignsOpen}
          onClose={closeCampaigns}
          onChanged={loadMeta}
          onFinished={() => { loadMeta(); loadList(); }}
          initialSetupOpen={openSetupFromLink}
          onRedo={(data) => { setCampaignsOpen(false); setRedo(data); setCampaignOpen(true); }}
        />
        <SignatureSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
        <NewOrgDialog
          open={newOrgOpen}
          onClose={() => setNewOrgOpen(false)}
          kinds={meta?.kinds || []}
          kommuner={(meta?.kommuner || []).filter((k) => k.nummer !== "9999")}
          defaultKommune={kommune}
          onCreated={(org) => {
            setNewOrgOpen(false);
            loadMeta();
            // Vis den nye organisasjonen: bytt til riktig kommune-fane (eller «Alle» for kommuner utenfor regionen) og åpne detaljene
            if (inCurrentArea(org)) loadList(); else setKommune(org.kommunenummer || "alle");
            setSelected(org);
          }}
        />

        <AlertDialog open={bulkConfirm} onOpenChange={(open) => !open && !deleting && setBulkConfirm(false)}>
          <AlertDialogContent data-testid="admin-prospects-bulk-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Slette {checkedItems.length} organisasjon{checkedItems.length === 1 ? "" : "er"} permanent?</AlertDialogTitle>
              <AlertDialogDescription>
                {checkedContacts ? `${checkedContacts} tilknyttede person${checkedContacts === 1 ? "" : "er"}, ` : ""}notater og e-postlogg slettes samtidig, og organisasjonene blokkeres mot å komme tilbake ved neste Brønnøysund-synk. Du kan gjenopprette dem én og én under «Slettede». Right in-arrangørkontoer påvirkes ikke.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <ul className="max-h-40 overflow-y-auto rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs space-y-0.5" data-testid="admin-prospects-bulk-names">
              {checkedItems.slice(0, 50).map((o) => <li key={o.id} className="truncate">{o.name}</li>)}
              {checkedItems.length > 50 && <li className="text-muted-foreground">… og {checkedItems.length - 50} til</li>}
            </ul>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting} data-testid="admin-prospects-bulk-cancel-dialog">Avbryt</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); confirmBulkDelete(); }}
                disabled={deleting}
                className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90"
                data-testid="admin-prospects-bulk-confirm"
              >
                {deleting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />} Slett {checkedItems.length} permanent
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && !deleting && setToDelete(null)}>
          <AlertDialogContent data-testid="admin-prospect-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Slette «{toDelete?.name}» permanent?</AlertDialogTitle>
              <AlertDialogDescription>
                Organisasjonen fjernes fra kontaktregisteret sammen med {toDelete?.contacts_active ?? 0} aktiv{(toDelete?.contacts_active ?? 0) === 1 ? "" : "e"} kontakt{(toDelete?.contacts_active ?? 0) === 1 ? "" : "er"}
                {toDelete?.contacts_suggested ? ` og ${toDelete.contacts_suggested} forslag` : ""}, notater og e-postlogg.
                {toDelete?.org_number
                  ? <> Org.nr {toDelete.org_number} blokkeres, så den ikke kommer tilbake ved neste Brønnøysund-synk. Du kan gjenopprette den under «Slettede».</>
                  : <> Organisasjonen er lagt inn manuelt (uten org.nr) og kan ikke gjenopprettes – men du kan legge den inn igjen senere.</>}
                {" "}Right in-arrangørkontoer påvirkes ikke.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting} data-testid="admin-prospect-delete-cancel">Avbryt</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); confirmDelete(); }}
                disabled={deleting}
                className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90"
                data-testid="admin-prospect-delete-confirm"
              >
                {deleting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1.5" />} Slett permanent
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <ExcludedDialog open={excludedOpen} onClose={() => setExcludedOpen(false)} onRestored={onRestored} />
        <SubscribersDialog open={subscribersOpen} onClose={() => setSubscribersOpen(false)} onChanged={loadMeta} />

        <Dialog open={importOpen} onOpenChange={setImportOpen}>
          <DialogContent data-testid="admin-prospects-import-dialog">
            <DialogHeader>
              <DialogTitle>Importer kontaktliste</DialogTitle>
              <DialogDescription>
                CSV eller Excel (.xlsx) med overskrifter. Vi kjenner igjen kolonnene <strong>Organisasjon</strong>, <strong>Org.nr</strong>, <strong>Navn</strong>, <strong>Rolle</strong>, <strong>E-post</strong> og <strong>Telefon</strong>.
                Kontaktene kobles til organisasjonen på org.nr (helst) eller nøyaktig navn. Samme e-post på samme organisasjon blir oppdatert, ikke duplisert.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.txt,.xlsx"
                className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-2 file:text-sm file:font-medium hover:file:bg-secondary/80"
                onChange={(e) => handleImport(e.target.files?.[0])}
                disabled={importing}
                data-testid="admin-prospects-import-file"
              />
              {importing && <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Importerer …</p>}
              {importResult && (
                <div className="rounded-md border border-border bg-secondary/50 p-3 text-sm space-y-1" data-testid="admin-prospects-import-result">
                  <p><strong>{importResult.imported}</strong> nye kontakter, <strong>{importResult.updated}</strong> oppdaterte, {importResult.skipped} tomme rader hoppet over.</p>
                  {importResult.unmatched_count > 0 && (
                    <p className="text-muted-foreground">
                      {importResult.unmatched_count} rad{importResult.unmatched_count === 1 ? "" : "er"} fant ikke organisasjonen: {importResult.unmatched.slice(0, 8).join(", ")}{importResult.unmatched.length > 8 ? " …" : ""}
                    </p>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

const infoFormFrom = (org) => ({
  name: org?.name || "",
  kind: org?.kind || "annet",
  kommunenummer: org?.kommunenummer || (org?.kommune ? "annen" : ""),
  kommune: org?.kommune || "",
  email: org?.email || "",
  phone: org?.phone || "",
  website: org?.website || "",
  adresse: org?.address?.adresse || "",
  postnummer: org?.address?.postnummer || "",
  poststed: org?.address?.poststed || "",
});

function ProspectDetailDialog({ org, loading, statuses, kinds = [], kommuner = [], onClose, onDelete, onOrgUpdate, onContactsChange }) {
  const [form, setForm] = useState({ followup_status: "ikke_kontaktet", notes: "", last_email_sent_at: "", last_email_subject: "" });
  const [saving, setSaving] = useState(false);
  const [infoEditing, setInfoEditing] = useState(false); // rediger kontaktinfo (e-post, telefon, nettside, adresse …)
  const [infoForm, setInfoForm] = useState(infoFormFrom(null));
  const [infoSaving, setInfoSaving] = useState(false);
  const [newContact, setNewContact] = useState({ name: "", role: "", email: "", phone: "" });
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null); // contact id
  const [editForm, setEditForm] = useState({});
  const [working, setWorking] = useState("");
  const [harvesting, setHarvesting] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const addNameRef = useRef(null);

  const focusAddContact = () => {
    addNameRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => addNameRef.current?.focus(), 350);
  };

  useEffect(() => {
    if (org) {
      setForm({
        followup_status: org.followup_status || "ikke_kontaktet",
        notes: org.notes || "",
        last_email_sent_at: org.last_email_sent_at || "",
        last_email_subject: org.last_email_subject || "",
      });
      setEditing(null);
      setInfoEditing(false);
      setNewContact({ name: "", role: "", email: "", phone: "" });
    }
  }, [org?.id, org?.followup_status, org?.notes, org?.last_email_sent_at, org?.last_email_subject]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!org) return null;
  const contacts = org.contacts || [];
  const active = contacts.filter((c) => c.status === "active");
  const suggested = contacts.filter((c) => c.status === "suggested");
  const isManualOrg = org.source === "manual";
  const manualFields = new Set(org.manual_fields || []);
  const dirty = form.followup_status !== (org.followup_status || "ikke_kontaktet") || form.notes !== (org.notes || "")
    || form.last_email_sent_at !== (org.last_email_sent_at || "") || form.last_email_subject !== (org.last_email_subject || "");

  const startInfoEdit = () => { setInfoForm(infoFormFrom(org)); setInfoEditing(true); };
  const saveInfo = async () => {
    setInfoSaving(true);
    try {
      const payload = {
        email: infoForm.email, phone: infoForm.phone, website: infoForm.website,
        adresse: infoForm.adresse, postnummer: infoForm.postnummer, poststed: infoForm.poststed,
        kind: infoForm.kind,
      };
      if (isManualOrg) {
        payload.name = infoForm.name;
        payload.kommunenummer = infoForm.kommunenummer === "annen" ? "" : infoForm.kommunenummer;
        payload.kommune = infoForm.kommunenummer === "annen" ? infoForm.kommune : "";
      }
      const res = await api.patch(`/admin/prospects/${org.id}`, payload);
      onOrgUpdate(res.data);
      setInfoEditing(false);
      toast.success("Kontaktinformasjon lagret");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setInfoSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`/admin/prospects/${org.id}`, form);
      onOrgUpdate(res.data);
      toast.success("Oppfølging lagret");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const addContact = async () => {
    setAdding(true);
    try {
      const res = await api.post(`/admin/prospects/${org.id}/contacts`, newContact);
      onContactsChange([...contacts, res.data]);
      setNewContact({ name: "", role: "", email: "", phone: "" });
      toast.success("Kontakt lagt til");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setAdding(false);
    }
  };

  const patchContact = async (c, patch, okMsg) => {
    setWorking(c.id);
    try {
      const res = await api.patch(`/admin/prospects/contacts/${c.id}`, patch);
      onContactsChange(contacts.map((x) => (x.id === c.id ? res.data : x)));
      setEditing(null);
      if (okMsg) toast.success(okMsg);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setWorking("");
    }
  };

  const deleteContact = async (c) => {
    setWorking(c.id);
    try {
      await api.delete(`/admin/prospects/contacts/${c.id}`);
      onContactsChange(contacts.filter((x) => x.id !== c.id));
      toast.success("Kontakten er slettet");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setWorking("");
    }
  };

  const harvest = async () => {
    setHarvesting(true);
    try {
      const res = await api.post(`/admin/prospects/${org.id}/harvest`);
      onContactsChange(res.data.contacts);
      onOrgUpdate({ id: org.id, website_harvest: res.data.summary });
      const s = res.data.summary;
      if (s.error && !s.found) toast.warning(`Fant ingen e-poster: ${s.error}`);
      else toast.success(`${s.pages_checked} sider sjekket – ${s.new_suggestions} nye forslag`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setHarvesting(false);
    }
  };

  const ContactRow = ({ c }) => {
    const isEditing = editing === c.id;
    return (
      <li className="py-2.5 flex flex-col gap-2" data-testid={`admin-contact-${c.id}`}>
        {isEditing ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder="Navn" data-testid={`admin-contact-edit-name-${c.id}`} />
            <Input value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} placeholder="Rolle" data-testid={`admin-contact-edit-role-${c.id}`} />
            <Input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} placeholder="E-post" type="email" data-testid={`admin-contact-edit-email-${c.id}`} />
            <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} placeholder="Telefon" data-testid={`admin-contact-edit-phone-${c.id}`} />
            <div className="sm:col-span-2 flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={() => setEditing(null)} data-testid={`admin-contact-edit-cancel-${c.id}`}>Avbryt</Button>
              <Button size="sm" onClick={() => patchContact(c, editForm, "Kontakt oppdatert")} disabled={working === c.id} data-testid={`admin-contact-edit-save-${c.id}`}>
                {working === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Lagre"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium flex flex-wrap items-center gap-1.5">
                {c.name || <span className="italic text-muted-foreground">Navn ukjent</span>}
                {c.role && <span className="text-muted-foreground font-normal">· {c.role}</span>}
                <Badge variant="secondary" className="text-[10px] font-normal">{SOURCE_LABELS[c.source] || c.source}</Badge>
                {c.newsletter === "confirmed" && <Badge variant="outline" className="text-[10px] font-normal border-brand/30 bg-brand-soft text-brand" data-testid={`admin-contact-newsletter-${c.id}`}><BellRing className="w-3 h-3 mr-1" /> Abonnent</Badge>}
                {c.newsletter === "unsubscribed" && <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground">Avmeldt nyhetsbrev</Badge>}
              </p>
              <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                {c.email ? <a href={`mailto:${c.email}`} className="flex items-center gap-1 text-brand underline underline-offset-4 break-all"><Mail className="w-3 h-3" />{c.email}</a> : <span className="italic">Ingen e-post</span>}
                {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</a>}
                {c.source === "website" && c.source_url && (
                  <a href={c.source_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 underline underline-offset-4"><ExternalLink className="w-3 h-3" />kilde</a>
                )}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {c.status === "suggested" ? (
                <>
                  <Button size="sm" variant="secondary" className="h-8 text-[hsl(var(--success))]" onClick={() => patchContact(c, { status: "active" }, "Forslag godkjent")} disabled={working === c.id} data-testid={`admin-contact-approve-${c.id}`}>
                    <Check className="w-4 h-4 mr-1" /> Godkjenn
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8" onClick={() => patchContact(c, { status: "rejected" }, "Forslag avvist")} disabled={working === c.id} data-testid={`admin-contact-reject-${c.id}`}>
                    <X className="w-4 h-4 mr-1" /> Avvis
                  </Button>
                </>
              ) : (
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditing(c.id); setEditForm({ name: c.name || "", role: c.role || "", email: c.email || "", phone: c.phone || "" }); }} title="Rediger" data-testid={`admin-contact-edit-${c.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
              <Button size="icon" variant="ghost" className="h-8 w-8 text-[hsl(var(--danger))]" onClick={() => deleteContact(c)} disabled={working === c.id} title="Slett personen" data-testid={`admin-contact-delete-${c.id}`}>
                {working === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        )}
      </li>
    );
  };

  return (
    <Dialog open={!!org} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="admin-prospect-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 pr-6">
            <span data-testid="admin-prospect-detail-name">{org.name}</span>
            <Badge variant="outline" className={`text-[11px] ${KIND_STYLES[org.kind] || KIND_STYLES.annet}`}>{org.kind_label}</Badge>
            {isManualOrg && <Badge variant="secondary" className="text-[11px]" data-testid="admin-prospect-detail-manual"><PenLine className="w-3 h-3 mr-1" /> Lagt inn manuelt</Badge>}
            {org.is_rightin_organizer && <Badge className="text-[11px] bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]"><Ticket className="w-3 h-3 mr-1" /> Right in-arrangør</Badge>}
            {org.newsletter_subscribed && <Badge variant="outline" className="text-[11px] border-brand/30 bg-brand-soft text-brand" data-testid="admin-prospect-detail-newsletter"><BellRing className="w-3 h-3 mr-1" /> Nyhetsbrev-abonnent</Badge>}
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <span data-testid="admin-prospect-detail-orgnr">{org.org_number ? `Org.nr ${org.org_number}` : "Uten org.nr"}</span>
            <span>{org.kommune || (isManualOrg ? "Kommune ikke oppgitt" : "")}{org.address?.poststed ? ` · ${org.address.postnummer ? `${org.address.postnummer} ` : ""}${org.address.poststed}` : ""}</span>
            {org.org_form && <span>{org.org_form_desc || org.org_form}</span>}
            {org.naering && <span>{org.naering}</span>}
            {org.is_subunit && org.parent_name && <span>Del av {org.parent_name}</span>}
            {org.in_frivillighetsregisteret && <span className="text-brand">Frivillighetsregisteret</span>}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-3"><Skeleton className="h-16 w-full" /><Skeleton className="h-32 w-full" /><Skeleton className="h-24 w-full" /></div>
        ) : (
          <div className="space-y-6">
            {/* Kontaktinfo (Brønnøysund eller lagt inn/endret av admin) */}
            <section className="rounded-xl border border-border bg-secondary/40 p-4 text-sm space-y-1.5" data-testid="admin-prospect-official">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-medium flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-brand" /> {isManualOrg ? "Kontaktinformasjon (lagt inn manuelt)" : "Offisiell kontaktinformasjon (Brønnøysund)"}
                </p>
                {!infoEditing && (
                  <Button size="sm" variant="secondary" className="h-8" onClick={startInfoEdit} data-testid="admin-prospect-info-edit-button">
                    <Pencil className="w-3.5 h-3.5 mr-1.5" /> {org.email || org.phone || org.website ? "Rediger" : "Legg inn e-post/telefon"}
                  </Button>
                )}
              </div>
              {infoEditing ? (
                <div className="space-y-2 pt-1" data-testid="admin-prospect-info-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {isManualOrg && (
                      <div className="space-y-1 sm:col-span-2">
                        <Label htmlFor="info-name">Navn</Label>
                        <Input id="info-name" value={infoForm.name} onChange={(e) => setInfoForm({ ...infoForm, name: e.target.value })} data-testid="admin-prospect-info-name" />
                      </div>
                    )}
                    <div className="space-y-1">
                      <Label>Type</Label>
                      <Select value={infoForm.kind} onValueChange={(v) => setInfoForm({ ...infoForm, kind: v })}>
                        <SelectTrigger className="bg-background" data-testid="admin-prospect-info-kind"><SelectValue /></SelectTrigger>
                        <SelectContent>{kinds.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {isManualOrg && (
                      <div className="space-y-1">
                        <Label>Kommune</Label>
                        <Select value={infoForm.kommunenummer || "annen"} onValueChange={(v) => setInfoForm({ ...infoForm, kommunenummer: v })}>
                          <SelectTrigger className="bg-background" data-testid="admin-prospect-info-kommune"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {kommuner.map((k) => <SelectItem key={k.nummer} value={k.nummer}>{k.navn}</SelectItem>)}
                            <SelectItem value="annen">Annen kommune (utenfor regionen)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {isManualOrg && (infoForm.kommunenummer === "annen" || !infoForm.kommunenummer) && (
                      <div className="space-y-1 sm:col-span-2">
                        <Label htmlFor="info-kommune-navn">Kommunenavn</Label>
                        <Input id="info-kommune-navn" value={infoForm.kommune} onChange={(e) => setInfoForm({ ...infoForm, kommune: e.target.value })} placeholder="F.eks. Bergen" data-testid="admin-prospect-info-kommune-navn" />
                      </div>
                    )}
                    <div className="space-y-1">
                      <Label htmlFor="info-email">E-post</Label>
                      <Input id="info-email" type="email" value={infoForm.email} onChange={(e) => setInfoForm({ ...infoForm, email: e.target.value })} placeholder="post@organisasjon.no" data-testid="admin-prospect-info-email" />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="info-phone">Telefon</Label>
                      <Input id="info-phone" value={infoForm.phone} onChange={(e) => setInfoForm({ ...infoForm, phone: e.target.value })} data-testid="admin-prospect-info-phone" />
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <Label htmlFor="info-website">Nettside</Label>
                      <Input id="info-website" value={infoForm.website} onChange={(e) => setInfoForm({ ...infoForm, website: e.target.value })} placeholder="www.organisasjon.no" data-testid="admin-prospect-info-website" />
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <Label htmlFor="info-adresse">Adresse</Label>
                      <Input id="info-adresse" value={infoForm.adresse} onChange={(e) => setInfoForm({ ...infoForm, adresse: e.target.value })} data-testid="admin-prospect-info-adresse" />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="info-postnr">Postnr</Label>
                      <PostalCodeInput id="info-postnr" value={infoForm.postnummer} onChange={(v) => setInfoForm((f) => ({ ...f, postnummer: v }))} onPlace={(p) => setInfoForm((f) => ({ ...f, poststed: p.poststed }))} limit={6} testId="admin-prospect-info-postnummer" />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="info-poststed">Poststed</Label>
                      <Input id="info-poststed" value={infoForm.poststed} onChange={(e) => setInfoForm({ ...infoForm, poststed: e.target.value })} data-testid="admin-prospect-info-poststed" />
                    </div>
                  </div>
                  {!isManualOrg && (
                    <p className="text-xs text-muted-foreground flex items-start gap-1.5"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Felt du endrer her beholdes ved neste Brønnøysund-synk (de overskrives ikke).</p>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setInfoEditing(false)} disabled={infoSaving} data-testid="admin-prospect-info-cancel">Avbryt</Button>
                    <Button size="sm" onClick={saveInfo} disabled={infoSaving || (isManualOrg && !infoForm.name.trim())} data-testid="admin-prospect-info-save">
                      {infoSaving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />} Lagre
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-muted-foreground">
                  {org.email ? <a href={`mailto:${org.email}`} className="flex items-center gap-1 text-brand underline underline-offset-4 break-all" data-testid="admin-prospect-official-email"><Mail className="w-3.5 h-3.5" />{org.email}{!isManualOrg && manualFields.has("email") && <span className="text-[10px] text-muted-foreground no-underline">(manuelt)</span>}</a> : <span className="italic" data-testid="admin-prospect-official-email-missing">Ingen e-post registrert</span>}
                  {org.org_email_newsletter === "confirmed" && <Badge variant="outline" className="text-[10px] border-brand/30 bg-brand-soft text-brand"><BellRing className="w-3 h-3 mr-1" /> Abonnerer på nyhetsbrev</Badge>}
                  {org.phone && <a href={`tel:${org.phone}`} className="flex items-center gap-1" data-testid="admin-prospect-official-phone"><Phone className="w-3.5 h-3.5" />{org.phone}</a>}
                  {org.website && <a href={org.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 underline underline-offset-4" data-testid="admin-prospect-official-website"><Globe className="w-3.5 h-3.5" />{org.website.replace(/^https?:\/\//, "")}</a>}
                  {(org.address?.adresse || org.address?.poststed) && (
                    <span className="flex items-center gap-1" data-testid="admin-prospect-official-address"><MapPin className="w-3.5 h-3.5" />{[org.address?.adresse, [org.address?.postnummer, org.address?.poststed].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</span>
                  )}
                </p>
              )}
              {org.rightin_org && (
                <p className="text-xs text-[hsl(var(--success))] flex items-center gap-1.5" data-testid="admin-prospect-rightin-info">
                  <Ticket className="w-3.5 h-3.5" /> Registrert på Right in som «{org.rightin_org.org_name}» ({org.rightin_org.email}) · {org.rightin_org.events} arrangement
                  {org.rightin_org.matched_by === "email" && <span className="text-muted-foreground">· koblet via e-post (org.nr mangler på Right in-kontoen)</span>}
                </p>
              )}
              {org.website_is_shared && (
                <p className="text-xs text-muted-foreground flex items-start gap-1.5" data-testid="admin-prospect-shared-website">
                  <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Nettsiden er registrert på {org.website_shared_count} organisasjoner (felles-/paraplyside). E-postforslag hentes derfor ikke automatisk – bruk «Hent fra nettside» hvis du vil.
                </p>
              )}
            </section>

            {org.kind === "kommunal" && org.missing_contact_info && (
              <div className="rounded-xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="admin-prospect-missing-callout">
                <div className="flex-1 text-sm">
                  <p className="font-medium flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-[hsl(var(--warning-foreground))]" /> Mangler kontaktinfo</p>
                  <p className="text-muted-foreground text-xs mt-0.5">Kommunale enheter har verken styreroller eller e-post i Brønnøysund. Legg inn kulturleder, biblioteksjef eller rektor ved kulturskolen manuelt.</p>
                </div>
                <Button size="sm" variant="secondary" onClick={focusAddContact} data-testid="admin-prospect-missing-add-button">
                  <UserPlus className="w-4 h-4 mr-1.5" /> Legg til kontaktperson
                </Button>
              </div>
            )}

            {/* Oppfølging */}
            <section className="space-y-3" data-testid="admin-prospect-followup">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-medium flex items-center gap-2"><CalendarDays className="w-4 h-4 text-brand" /> Oppfølging</p>
                <Button size="sm" onClick={() => setEmailOpen(true)} data-testid="admin-prospect-send-email-button">
                  <Send className="w-4 h-4 mr-1.5" /> Send e-post
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <Select value={form.followup_status} onValueChange={(v) => setForm({ ...form, followup_status: v })}>
                    <SelectTrigger data-testid="admin-prospect-followup-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{statuses.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="last-email">Siste e-post sendt fra Kviteberg Teknologi</Label>
                  <Input id="last-email" type="date" value={form.last_email_sent_at} onChange={(e) => setForm({ ...form, last_email_sent_at: e.target.value })} data-testid="admin-prospect-last-email-date" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="last-subject">Emne / hva ble sendt</Label>
                  <Input id="last-subject" value={form.last_email_subject} onChange={(e) => setForm({ ...form, last_email_subject: e.target.value })} placeholder="F.eks. «Tilbud om Right in til høsten»" data-testid="admin-prospect-last-email-subject" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="notes">Notat</Label>
                  <Textarea id="notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Ringt, avtalt møte, hvem er rett person …" data-testid="admin-prospect-notes" />
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={save} disabled={!dirty || saving} data-testid="admin-prospect-save-followup">
                  {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />} Lagre oppfølging
                </Button>
              </div>
              {(org.replies || []).length > 0 && (
                <div className="rounded-xl border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5 p-3 text-sm space-y-2" data-testid="admin-prospect-replies">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-medium flex items-center gap-1.5 text-xs uppercase tracking-wide text-[hsl(var(--success))]"><MessageSquareReply className="w-3.5 h-3.5" /> Svar fra organisasjonen ({org.replies.length})</p>
                    <Link to={`/dashboard/svar?reply=${org.replies[0].id}`} className="text-xs text-brand underline-offset-2 hover:underline" data-testid="admin-prospect-replies-open-inbox">Les og svar i innboksen</Link>
                  </div>
                  <ul className="space-y-2">
                    {org.replies.map((r) => (
                      <li key={r.id} className="rounded-lg border border-border bg-background p-2.5 text-xs space-y-1" data-testid={`admin-prospect-reply-${r.id}`} data-kind={r.kind}>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          <span className="tabular-nums text-muted-foreground">{formatDateTime(r.received_at)}</span>
                          <span className="font-medium text-foreground break-all">{r.from_name ? `${r.from_name} <${r.from_email}>` : r.from_email}</span>
                          {r.kind !== "reply" && <Badge variant="secondary" className="text-[10px] font-normal">{r.kind_label}</Badge>}
                          {r.status_changed && <Badge variant="outline" className={`text-[10px] font-normal ${STATUS_STYLES.interessert}`}>Sett til Interessert</Badge>}
                          {r.campaign_subject && <span className="text-muted-foreground">· svar på «{r.campaign_subject}»</span>}
                        </div>
                        {r.subject && <p className="font-medium">{r.subject}</p>}
                        <p className="whitespace-pre-line text-muted-foreground line-clamp-4">{r.excerpt || "(tom melding)"}</p>
                        {r.kind === "reply" && (
                          <p className="text-muted-foreground">
                            {r.forwarded_ok ? `Videresendt til ${r.forwarded_to}` : r.forward_error ? `Videresending feilet: ${r.forward_error}` : "Ikke videresendt"}
                            {r.attachments?.length ? ` · ${r.attachments.length} vedlegg` : ""}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(org.email_log || []).length > 0 && (
                <div className="rounded-xl border border-border bg-secondary/40 p-3 text-sm space-y-1.5" data-testid="admin-prospect-email-log">
                  <p className="font-medium flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground"><History className="w-3.5 h-3.5" /> Sendte e-poster fra Right in</p>
                  <ul className="space-y-1">
                    {org.email_log.map((m) => (
                      <li key={m.id} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs" data-testid={`admin-prospect-email-log-${m.id}`}>
                        <span className="tabular-nums text-muted-foreground">{formatDateTime(m.at)}</span>
                        <span className="font-medium text-foreground">{m.subject}</span>
                        <span className="text-muted-foreground">til {m.to.length} mottaker{m.to.length === 1 ? "" : "e"}</span>
                        {!m.ok && <Badge variant="outline" className="text-[10px] text-[hsl(var(--danger))] border-[hsl(var(--danger))]/40">Feilet</Badge>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            {/* Kontakter */}
            <section className="space-y-3" data-testid="admin-prospect-contacts">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-medium flex items-center gap-2"><UserPlus className="w-4 h-4 text-brand" /> Kontaktpersoner <span className="text-muted-foreground font-normal text-sm">({active.length})</span></p>
                {org.website && (
                  <Button size="sm" variant="secondary" onClick={harvest} disabled={harvesting} data-testid="admin-prospect-harvest-button">
                    {harvesting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />} Hent fra nettside
                  </Button>
                )}
              </div>
              {org.website_harvest?.at && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="admin-prospect-harvest-summary">
                  <Info className="w-3.5 h-3.5" /> Nettside sjekket {formatDateTime(org.website_harvest.at)}: {org.website_harvest.pages_checked} sider, {org.website_harvest.found} e-poster funnet{org.website_harvest.error ? ` (${org.website_harvest.error})` : ""}
                </p>
              )}
              {suggested.length > 0 && (
                <div className="rounded-xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-3" data-testid="admin-prospect-suggestions">
                  <p className="text-sm font-medium mb-1">Forslag fra nettsiden – godkjenn eller avvis ({suggested.length})</p>
                  <ul className="divide-y divide-border">{suggested.map((c) => <ContactRow key={c.id} c={c} />)}</ul>
                </div>
              )}
              {active.length === 0 ? (
                <p className="text-sm text-muted-foreground italic" data-testid="admin-prospect-contacts-empty">{isManualOrg ? "Ingen kontaktpersoner ennå – legg inn under." : "Ingen kontaktpersoner ennå – roller fra Brønnøysund kommer ved synk, eller legg inn manuelt under."}</p>
              ) : (
                <ul className="divide-y divide-border rounded-xl border border-border px-3">{active.map((c) => <ContactRow key={c.id} c={c} />)}</ul>
              )}
              <div className="rounded-xl border border-dashed border-border p-3 space-y-2" data-testid="admin-prospect-add-contact">
                <p className="text-sm font-medium">Legg til kontaktperson</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Input ref={addNameRef} value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} placeholder="Navn" data-testid="admin-prospect-new-name" />
                  <Input value={newContact.role} onChange={(e) => setNewContact({ ...newContact, role: e.target.value })} placeholder={org.kind === "kommunal" ? "Rolle (f.eks. kulturleder, biblioteksjef)" : "Rolle (f.eks. leder, kasserer)"} data-testid="admin-prospect-new-role" />
                  <Input value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} placeholder="E-post" type="email" data-testid="admin-prospect-new-email" />
                  <Input value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} placeholder="Telefon" data-testid="admin-prospect-new-phone" />
                </div>
                <div className="flex justify-end">
                  <Button size="sm" onClick={addContact} disabled={adding || (!newContact.name.trim() && !newContact.email.trim())} data-testid="admin-prospect-add-contact-button">
                    {adding ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1.5" />} Legg til
                  </Button>
                </div>
              </div>
            </section>

            <div className="flex justify-end border-t border-border pt-3">
              <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-[hsl(var(--danger))]" onClick={() => onDelete(org)} data-testid="admin-prospect-delete-button">
                <Trash2 className="w-4 h-4 mr-1.5" /> Slett organisasjonen permanent
              </Button>
            </div>
          </div>
        )}
        <SendEmailDialog
          org={org}
          open={emailOpen}
          onClose={() => setEmailOpen(false)}
          onSent={(res) => {
            onOrgUpdate({ ...res.org, email_log: res.email_log });
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

function SendEmailDialog({ org, open, onClose, onSent }) {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chosen, setChosen] = useState([]);
  const [extra, setExtra] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [setStatus, setSetStatus] = useState(true);
  const [ccMe, setCcMe] = useState(false);
  const [sending, setSending] = useState(false);
  const [lang, setLang] = useState("nn");
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    if (!open) { setLang("nn"); setPreviewOpen(false); }
  }, [open]);

  useEffect(() => {
    if (!open || !org) return;
    setLoading(true);
    setExtra("");
    setSetStatus((org.followup_status || "ikke_kontaktet") === "ikke_kontaktet");
    api.get(`/admin/prospects/${org.id}/email-draft`, { params: { lang } })
      .then((res) => {
        setDraft(res.data);
        setSubject(res.data.subject);
        setBody(res.data.body);
        setChosen(res.data.recipients.filter((r) => r.default).map((r) => r.email));
      })
      .catch((err) => { toast.error(errorMessage(err)); onClose(); })
      .finally(() => setLoading(false));
  }, [open, org?.id, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const extraEmails = extra.split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean);
  const allTo = [...chosen, ...extraEmails];
  const toggle = (email) => setChosen((prev) => (prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email]));

  const send = async () => {
    setSending(true);
    try {
      const res = await api.post(`/admin/prospects/${org.id}/send-email`, {
        to: allTo, subject, body, set_status: setStatus ? "kontaktet" : null, cc_me: ccMe, lang,
      });
      toast.success(`E-post sendt til ${res.data.sent_to.length} mottaker${res.data.sent_to.length === 1 ? "" : "e"}`);
      onSent(res.data);
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="admin-prospect-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-brand" /> Send e-post til {org?.name}</DialogTitle>
          <DialogDescription>
            Sendes fra <strong>{draft?.sender || "Right in"}</strong>{draft?.reply_to ? <> med svar til <strong>{draft.reply_to}</strong></> : null}. Dato og emne loggføres automatisk på organisasjonen.
          </DialogDescription>
        </DialogHeader>
        {loading || !draft ? (
          <div className="space-y-3"><Skeleton className="h-10 w-full" /><Skeleton className="h-40 w-full" /></div>
        ) : (
          <div className="space-y-4">
            {!draft.enabled && (
              <Alert className="border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/10" data-testid="admin-prospect-email-disabled">
                <ShieldAlert className="h-4 w-4" />
                <AlertTitle>E-post er ikke konfigurert</AlertTitle>
                <AlertDescription className="text-sm">Serveren mangler SMTP-oppsett (Brevo). Legg inn SMTP-variablene og prøv igjen.</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label>Mottakere</Label>
              {draft.recipients.length === 0 ? (
                <p className="text-sm text-muted-foreground italic" data-testid="admin-prospect-email-no-recipients">Ingen e-postadresser på organisasjonen ennå – legg til en kontaktperson, eller skriv inn en adresse under.</p>
              ) : (
                <ul className="rounded-xl border border-border divide-y divide-border" data-testid="admin-prospect-email-recipients">
                  {draft.recipients.map((r) => (
                    <li key={r.email}>
                      <label className="flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-secondary/60">
                        <Checkbox checked={chosen.includes(r.email)} onCheckedChange={() => toggle(r.email)} className="mt-0.5" data-testid={`admin-prospect-email-recipient-${r.email}`} />
                        <span className="min-w-0 text-sm">
                          <span className="block break-all">{r.email}</span>
                          <span className="block text-xs text-muted-foreground">{r.label}</span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
              <Input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="Flere mottakere (skill med komma)" data-testid="admin-prospect-email-extra" />
            </div>
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label htmlFor="email-subject">Emne</Label>
                <Tabs value={lang} onValueChange={setLang}>
                  <TabsList className="h-8" data-testid="admin-prospect-email-lang-tabs">
                    {(draft.langs || ["nn", "nb"]).map((l) => (
                      <TabsTrigger key={l} value={l} className="h-6 px-2.5 text-xs" data-testid={`admin-prospect-email-lang-${l}`}>{EMAIL_LANG_LABELS[l] || l}</TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>
              <Input id="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} data-testid="admin-prospect-email-subject" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email-body">Tekst</Label>
              <Textarea id="email-body" rows={12} value={body} onChange={(e) => setBody(e.target.value)} maxLength={8000} className="font-sans text-sm" data-testid="admin-prospect-email-body" />
              <p className="text-xs text-muted-foreground" data-testid="admin-prospect-email-hint">
                Ren tekst – blank linje gir nytt avsnitt, lenker blir klikkbare. Right in-bildet legges alltid til øverst. Knapper skriver du som <code className="px-1 rounded bg-secondary">{"[Knappetekst](lenke)"}</code> på egen linje – små, i bildets farger.
                Hver mottaker får sin <strong>egen</strong> «Abonner på nyhetsbrev»-lenke der <code className="px-1 rounded bg-secondary">{"{nyhetsbrev}"}</code> står. Mangler teksten knapper, legges «{draft.cta || "Sjå her"}» og abonnér-lenken til nederst.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch checked={setStatus} onCheckedChange={setSetStatus} data-testid="admin-prospect-email-set-status" /> Sett status til «Kontaktet»
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch checked={ccMe} onCheckedChange={setCcMe} data-testid="admin-prospect-email-cc-me" /> Send kopi til meg
              </label>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
              <Button variant="outline" onClick={() => setPreviewOpen(true)} disabled={!subject.trim() || !body.trim()} className="sm:mr-auto" data-testid="admin-prospect-email-preview-button">
                <Eye className="w-4 h-4 mr-1.5" /> Forhåndsvis
              </Button>
              <Button variant="ghost" onClick={onClose} data-testid="admin-prospect-email-cancel">Avbryt</Button>
              <Button onClick={send} disabled={sending || !draft.enabled || allTo.length === 0 || !subject.trim() || !body.trim()} data-testid="admin-prospect-email-send">
                {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                Send til {allTo.length} mottaker{allTo.length === 1 ? "" : "e"}
              </Button>
            </div>
            <EmailPreviewDialog
              open={previewOpen}
              onClose={() => setPreviewOpen(false)}
              subject={subject}
              body={body}
              lang={lang}
              orgId={org?.id}
              recipients={[
                ...draft.recipients.filter((r) => allTo.includes(r.email)).map((r) => ({ email: r.email, name: r.name || "", org_id: org?.id, org_name: "" })),
                ...extraEmails.map((e) => ({ email: e, name: "", org_id: org?.id, org_name: "" })),
              ]}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ExcludedDialog({ open, onClose, onRestored }) {
  const [items, setItems] = useState(null);
  const [working, setWorking] = useState("");

  useEffect(() => {
    if (!open) return;
    setItems(null);
    api.get("/admin/prospects/excluded")
      .then((res) => setItems(res.data.items))
      .catch((err) => { toast.error(errorMessage(err)); setItems([]); });
  }, [open]);

  const restore = async (it) => {
    setWorking(it.org_number);
    try {
      const res = await api.post(`/admin/prospects/excluded/${it.org_number}/restore`);
      setItems((prev) => prev.filter((x) => x.org_number !== it.org_number));
      if (res.data.restored) toast.success(`«${it.name}» er gjenopprettet med roller fra Brønnøysund`);
      else toast.warning(res.data.detail || "Blokkeringen er fjernet");
      onRestored(res.data.org);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setWorking("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="admin-prospects-excluded-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Archive className="w-5 h-5 text-brand" /> Slettede organisasjoner</DialogTitle>
          <DialogDescription>
            Disse er slettet permanent og blokkert mot Brønnøysund-synk. «Gjenopprett» henter organisasjonen og styrerollene på nytt – notater, egne kontakter og e-postlogg er borte.
          </DialogDescription>
        </DialogHeader>
        {items === null ? (
          <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground italic py-4" data-testid="admin-prospects-excluded-empty">Ingen slettede organisasjoner.</p>
        ) : (
          <ul className="divide-y divide-border rounded-xl border border-border" data-testid="admin-prospects-excluded-list">
            {items.map((it) => (
              <li key={it.org_number} className="flex items-center gap-3 px-3 py-2.5" data-testid={`admin-prospects-excluded-${it.org_number}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{it.name}</p>
                  <p className="text-xs text-muted-foreground flex flex-wrap gap-x-2">
                    <span>Org.nr {it.org_number}</span><span>{it.kommune}</span><span>{it.kind_label}</span>
                    <span>Slettet {formatDateTime(it.deleted_at)}</span>
                    {it.contacts_deleted ? <span>{it.contacts_deleted} person{it.contacts_deleted === 1 ? "" : "er"} fjernet</span> : null}
                  </p>
                </div>
                <Button size="sm" variant="secondary" onClick={() => restore(it)} disabled={working === it.org_number} data-testid={`admin-prospects-restore-${it.org_number}`}>
                  {working === it.org_number ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1.5" />} Gjenopprett
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

const SUB_STATUS_STYLE = {
  confirmed: "border-brand/30 bg-brand-soft text-brand",
  pending: "bg-[hsl(var(--warning))]/20 text-[hsl(var(--warning-foreground))] border-[hsl(var(--warning))]/40",
  unsubscribed: "bg-muted text-muted-foreground border-border",
};

function SubscribersDialog({ open, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("confirmed");
  const [q, setQ] = useState("");
  const [working, setWorking] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/newsletter/subscribers", { params: { status: status !== "alle" ? status : undefined, q: q.trim() || undefined } })
      .then((res) => setData(res.data))
      .catch((err) => { toast.error(errorMessage(err)); setData({ items: [], counts: {} }); });
  }, [status, q]);

  useEffect(() => {
    if (!open) return;
    setData(null);
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [open, load]);

  const remove = async (it) => {
    setWorking(it.id);
    try {
      await api.delete(`/admin/newsletter/subscribers/${it.id}`);
      setData((prev) => ({ ...prev, items: prev.items.filter((x) => x.id !== it.id) }));
      toast.success("Abonnenten er slettet");
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setWorking("");
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/newsletter/export.csv", { params: { status: status !== "alle" ? status : "" }, responseType: "blob" });
      downloadBlob(res.data, `nyhetsbrev-abonnenter-${status}.csv`);
      toast.success("CSV lastet ned");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setExporting(false);
    }
  };

  const counts = data?.counts || {};
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="admin-newsletter-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BellRing className="w-5 h-5 text-brand" /> Nyhetsbrev-abonnenter</DialogTitle>
          <DialogDescription>
            Alle som aktivt har sagt ja til nyhetsbrev fra Right in – via lenken i e-postene dine eller på rightin.no/nyhetsbrev. Samtykketekst og tidspunkt lagres på hver abonnent.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col sm:flex-row gap-2">
          <Tabs value={status} onValueChange={setStatus} className="flex-1">
            <TabsList className="h-auto flex-wrap justify-start" data-testid="admin-newsletter-status-tabs">
              <TabsTrigger value="confirmed" data-testid="admin-newsletter-tab-confirmed">Abonnenter <span className="ml-1 text-xs tabular-nums text-muted-foreground">{counts.confirmed ?? ""}</span></TabsTrigger>
              <TabsTrigger value="pending" data-testid="admin-newsletter-tab-pending">Venter <span className="ml-1 text-xs tabular-nums text-muted-foreground">{counts.pending ?? ""}</span></TabsTrigger>
              <TabsTrigger value="unsubscribed" data-testid="admin-newsletter-tab-unsubscribed">Avmeldt <span className="ml-1 text-xs tabular-nums text-muted-foreground">{counts.unsubscribed ?? ""}</span></TabsTrigger>
              <TabsTrigger value="alle" data-testid="admin-newsletter-tab-alle">Alle</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button variant="secondary" size="sm" onClick={exportCsv} disabled={exporting} data-testid="admin-newsletter-export">
            {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} CSV
          </Button>
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk på e-post, navn eller organisasjon …" data-testid="admin-newsletter-search" />
        {data === null ? (
          <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>
        ) : data.items.length === 0 ? (
          <p className="text-sm text-muted-foreground italic py-4" data-testid="admin-newsletter-empty">
            {status === "confirmed" ? "Ingen abonnenter ennå. Lenken «Abonner på nyhetsbrevet» følger med i alle e-poster du sender fra registeret." : "Ingen treff."}
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-xl border border-border" data-testid="admin-newsletter-list">
            {data.items.map((it) => (
              <li key={it.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`admin-newsletter-item-${it.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium break-all flex flex-wrap items-center gap-1.5">
                    {it.email}
                    <Badge variant="outline" className={`text-[10px] font-normal ${SUB_STATUS_STYLE[it.status] || ""}`}>{it.status_label}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground flex flex-wrap gap-x-2">
                    {it.name && <span>{it.name}</span>}
                    {it.org_name && <span>{it.org_name}</span>}
                    <span>{it.source === "invitation" ? "Via e-post fra registeret" : it.source === "web" ? "Via rightin.no/nyhetsbrev" : it.source}</span>
                    {it.confirmed_at && <span>Bekreftet {formatDateTime(it.confirmed_at)}</span>}
                    {it.unsubscribed_at && <span>Avmeldt {formatDateTime(it.unsubscribed_at)}</span>}
                  </p>
                </div>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-[hsl(var(--danger))]" onClick={() => remove(it)} disabled={working === it.id} title="Slett abonnenten" data-testid={`admin-newsletter-delete-${it.id}`}>
                  {working === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Planlegg (eller send nå) samme standardmal til flere organisasjoner – personlig per mottaker, valgt tidspunkt i norsk tid. */
function CampaignDialog({ open, orgs, onClose, onCreated, redo = null, target = null }) {
  const [template, setTemplate] = useState(null);
  const [preview, setPreview] = useState(null);
  const [lang, setLang] = useState("nn");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [includeContacts, setIncludeContacts] = useState(true);
  const [when, setWhen] = useState("schedule"); // now | schedule
  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [setStatus, setSetStatus] = useState(true);
  const [ccMe, setCcMe] = useState(true);
  const [showSkipped, setShowSkipped] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false); // «Lag på nytt»: vis hvem som er tatt ut
  const [submitting, setSubmitting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [skipRecent, setSkipRecent] = useState(true); // områdeutsending: hopp over dei som fekk e-post siste 90 dagar
  const [skipClosed, setSkipClosed] = useState(true); // områdeutsending: hopp over «ikke aktuell», «registrert» og Right in-arrangørar
  const orgIds = useMemo(() => orgs.map((o) => o.id), [orgs]);
  const orgKey = orgIds.join(",");
  const isArea = !!target;
  const targetBody = useMemo(() => (target ? { ...target, skip_recent_days: skipRecent ? 90 : 0, skip_closed: skipClosed } : null), [target, skipRecent, skipClosed]);
  const targetKey = JSON.stringify(targetBody);

  // Nullstill når dialogen åpnes: standard = nynorsk, alle kontaktpersoner, i morgen kl. 09:00 norsk tid
  useEffect(() => {
    if (!open) return;
    setLang(redo?.source?.lang || "nn");
    setIncludeContacts(redo ? !!redo.source?.include_contacts : true);
    setWhen("schedule");
    setDate(osloDateStr(new Date(Date.now() + 24 * 3600 * 1000)));
    setTime("09:00");
    setSetStatus(true);
    setCcMe(true);
    setShowSkipped(false);
    setShowExcluded(false);
    setSkipRecent(true);
    setSkipClosed(true);
    setTemplate(null);
    setPreview(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Standardmalen på valgt språk (bytte av språk henter malen på nytt)
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get("/admin/prospects/campaigns/template", { params: { lang } })
      .then((res) => {
        if (cancelled) return;
        setTemplate(res.data);
        setSubject(res.data.subject);
        setBody(res.data.body);
      })
      .catch((err) => { if (!cancelled) { toast.error(errorMessage(err)); onClose(); } });
    return () => { cancelled = true; };
  }, [open, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hvem får e-post? (offisiell adresse + evt. alle kontaktpersoner med e-post – aldri samme adresse to ganger)
  useEffect(() => {
    if (!open || (orgIds.length === 0 && !targetBody)) return;
    let cancelled = false;
    setPreview(null);
    const payload = targetBody ? { target: targetBody, include_contacts: includeContacts } : { org_ids: orgIds, include_contacts: includeContacts };
    api.post("/admin/prospects/campaigns/preview", payload)
      .then((res) => { if (!cancelled) setPreview(res.data); })
      .catch((err) => { if (!cancelled) { toast.error(errorMessage(err)); setPreview({ recipients: [], skipped: [], recipients_count: 0, orgs_count: 0, with_name: 0 }); } });
    return () => { cancelled = true; };
  }, [open, orgKey, targetKey, includeContacts]); // eslint-disable-line react-hooks/exhaustive-deps

  const scheduledIso = when === "schedule" ? osloToIso(date, time) : null;
  const scheduledInPast = when === "schedule" && scheduledIso && new Date(scheduledIso).getTime() < Date.now() - 60 * 1000;
  const scheduleInvalid = when === "schedule" && (!scheduledIso || scheduledInPast);
  const recipientsCount = preview?.recipients_count ?? 0;
  const selectedCount = isArea ? (preview?.target?.selected ?? 0) : orgIds.length;
  const tooMany = template?.max_orgs && selectedCount > template.max_orgs;
  const canSubmit = !!template?.enabled && !!preview && recipientsCount > 0 && subject.trim() && body.trim() && !scheduleInvalid && !submitting && !tooMany;

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/admin/prospects/campaigns", {
        ...(targetBody ? { target: targetBody } : { org_ids: orgIds }), subject, body, lang, include_contacts: includeContacts,
        scheduled_at: when === "schedule" ? scheduledIso : null, set_status: setStatus ? "kontaktet" : null, cc_me: ccMe,
      });
      const c = res.data;
      if (c.status === "scheduled" && when === "schedule") {
        toast.success(`Utsending planlagt: ${c.recipients_count} e-post${c.recipients_count === 1 ? "" : "er"} sendes ${fmtOslo(c.scheduled_at)} (norsk tid)`);
      } else {
        toast.success(`Utsendingen er startet – ${c.recipients_count} e-post${c.recipients_count === 1 ? "" : "er"} sendes nå, én per sekund`);
      }
      onCreated(c);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const loading = !template;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto [&>*]:min-w-0" data-testid="admin-campaign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-brand" />
            {isArea
              ? <>Send e-post til {preview?.target?.label || "hele området"}</>
              : <>Send e-post til {orgs.length} organisasjon{orgs.length === 1 ? "" : "er"}</>}
          </DialogTitle>
          <DialogDescription>
            Samme standardmal til alle – men hver mottaker får sin egen e-post med organisasjonsnavn og personlig hilsen.
            Sendes fra <strong>{template?.sender || "Right in"}</strong>{template?.reply_to ? <> med svar til <strong>{template.reply_to}</strong></> : null}, én e-post per sekund. Dato og emne loggføres på hver organisasjon.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="space-y-3" data-testid="admin-campaign-loading"><Skeleton className="h-16 w-full" /><Skeleton className="h-10 w-full" /><Skeleton className="h-40 w-full" /></div>
        ) : (
          <div className="space-y-5">
            {!template.enabled && (
              <Alert className="border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/10" data-testid="admin-campaign-email-disabled">
                <ShieldAlert className="h-4 w-4" />
                <AlertTitle>E-post er ikke konfigurert</AlertTitle>
                <AlertDescription className="text-sm">Serveren mangler SMTP-oppsett (Brevo). Legg inn SMTP-variablene og prøv igjen.</AlertDescription>
              </Alert>
            )}
            {redo && (
              <Alert className="border-brand/30 bg-brand-soft/40" data-testid="admin-campaign-redo-info">
                <RotateCcw className="h-4 w-4" />
                <AlertTitle>Lager på nytt fra «{redo.source?.subject}»</AlertTitle>
                <AlertDescription className="text-sm space-y-1">
                  <p>
                    Samme organisasjoner som i utsendingen {redo.source?.status === "cancelled" ? "som ble avbrutt" : "fra"} {fmtOslo(redo.source?.scheduled_at)} – med fersk mal og riktig signatur.
                    {redo.excluded?.length ? <> <strong>{redo.excluded.length}</strong> er tatt ut: {redo.excluded_summary.map((x) => `${x.count} ${x.reason}`).join(", ")}.</> : <> Ingen er tatt ut.</>}
                  </p>
                  {redo.excluded?.length > 0 && (
                    <div>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setShowExcluded((v) => !v)} data-testid="admin-campaign-redo-toggle-excluded">
                        {showExcluded ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />}
                        {showExcluded ? "Skjul" : "Vis"} hvem som er tatt ut
                      </Button>
                      {showExcluded && (
                        <ul className="mt-1 max-h-28 overflow-y-auto rounded-md border border-border bg-background px-3 py-2 text-xs space-y-0.5" data-testid="admin-campaign-redo-excluded-list">
                          {redo.excluded.map((x) => <li key={x.org_id} className="truncate">{x.org_name} – {x.reason}</li>)}
                        </ul>
                      )}
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}
            {redo && orgs.length === 0 && (
              <Alert className="border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10" data-testid="admin-campaign-redo-empty">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Ingen igjen å sende til</AlertTitle>
                <AlertDescription className="text-sm">Alle organisasjonene fra den forrige utsendingen har svart, er registrert eller er tatt ut.</AlertDescription>
              </Alert>
            )}
            {tooMany && (
              <Alert className="border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10" data-testid="admin-campaign-too-many">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>For mange organisasjoner</AlertTitle>
                <AlertDescription className="text-sm">Maks {template.max_orgs} organisasjoner per utsending – del opp i flere utsendinger.</AlertDescription>
              </Alert>
            )}

            {/* Mottakere */}
            <section className="rounded-xl border border-border bg-secondary/40 p-4 space-y-3" data-testid="admin-campaign-recipients">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-heading font-semibold flex items-center gap-2"><Users className="w-4 h-4 text-brand" /> Mottakere</p>
                  {preview ? (
                    <p className="text-sm text-muted-foreground mt-0.5" data-testid="admin-campaign-recipients-summary">
                      <strong className="text-foreground">{preview.recipients_count}</strong> e-post{preview.recipients_count === 1 ? "" : "er"} til <strong className="text-foreground">{preview.orgs_count}</strong> organisasjon{preview.orgs_count === 1 ? "" : "er"}
                      {preview.with_name ? <> · {preview.with_name} med personlig navn i hilsenen</> : null}
                      {preview.skipped.length ? <> · <span className="text-[hsl(var(--warning-foreground))]">{preview.skipped.length} uten e-postadresse hoppes over</span></> : null}
                    </p>
                  ) : (
                    <Skeleton className="h-5 w-64 mt-1" />
                  )}
                </div>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <Switch checked={includeContacts} onCheckedChange={setIncludeContacts} data-testid="admin-campaign-include-contacts" /> Også alle kontaktpersoner med e-post
                </label>
              </div>
              {isArea && (
                <div className="rounded-lg border border-border bg-background px-3 py-2.5 space-y-2" data-testid="admin-campaign-target">
                  <p className="text-xs text-muted-foreground">
                    <MapPin className="w-3.5 h-3.5 inline mr-1 text-brand" />
                    {preview?.target
                      ? <>Område <strong className="text-foreground">{preview.target.label}</strong>: {preview.target.total} organisasjoner i registeret
                          {preview.target.skipped_closed ? <> · {preview.target.skipped_closed} hoppet over (ikke aktuell / registrert / allerede Right in-arrangør)</> : null}
                          {preview.target.skipped_recent ? <> · {preview.target.skipped_recent} hoppet over (fikk e-post siste 90 dager)</> : null}
                          {" "}· <strong className="text-foreground">{preview.target.selected}</strong> igjen</>
                      : "Henter området …"}
                  </p>
                  <div className="flex flex-wrap gap-x-5 gap-y-1.5">
                    <label className="flex items-center gap-2 cursor-pointer text-xs">
                      <Switch checked={skipRecent} onCheckedChange={setSkipRecent} data-testid="admin-campaign-skip-recent" /> Hopp over de som fikk e-post siste 90 dager
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-xs">
                      <Switch checked={skipClosed} onCheckedChange={setSkipClosed} data-testid="admin-campaign-skip-closed" /> Hopp over «ikke aktuell», «registrert» og Right in-arrangører
                    </label>
                  </div>
                </div>
              )}
              {preview && preview.recipients.length > 0 && (
                <ul className="max-h-36 overflow-y-auto rounded-md border border-border bg-background px-3 py-2 text-xs space-y-0.5" data-testid="admin-campaign-recipient-list">
                  {preview.recipients.slice(0, 200).map((r) => (
                    <li key={r.email} className="flex flex-wrap gap-x-2 min-w-0">
                      <span className="font-medium break-words">{r.org_name}</span>
                      <span className="text-muted-foreground break-all">{r.name ? `${r.name} · ` : ""}{r.email}</span>
                    </li>
                  ))}
                  {preview.recipients.length > 200 && <li className="text-muted-foreground">… og {preview.recipients.length - 200} til</li>}
                </ul>
              )}
              {preview && preview.recipients_count === 0 && (
                <p className="text-sm text-[hsl(var(--danger))]" data-testid="admin-campaign-no-recipients">Ingen av de valgte organisasjonene har e-postadresse – legg til kontaktpersoner først.</p>
              )}
              {preview && preview.recipients_count > 0 && template?.daily_limit > 0 && (() => {
                const limit = template.daily_limit;
                const left = template.quota?.window_end ? Math.max(0, template.quota.remaining ?? limit) : limit;
                const n = preview.recipients_count;
                const days = n <= left ? 1 : 1 + Math.ceil((n - left) / limit);
                return (
                  <p className="text-xs text-muted-foreground flex items-start gap-1.5" data-testid="admin-campaign-quota-hint">
                    <Clock className="w-3.5 h-3.5 mt-0.5 text-brand shrink-0" />
                    <span>
                      Dagsgrensen er {limit} e-poster per døgn (samlet for alle utsendinger){template.quota?.window_end ? `, ${left} igjen i dette døgnet` : ""}.
                      {days > 1
                        ? ` Denne utsendingen tar derfor ca. ${days} dager – den pauser når grensen er nådd og fortsetter automatisk ved samme klokkeslett neste dag.`
                        : " Alle e-postene får plass i dette døgnet."}
                      {" "}Grensen kan endres under «Innstillinger».
                    </span>
                  </p>
                );
              })()}
              {preview && preview.skipped.length > 0 && (
                <div>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground" onClick={() => setShowSkipped((v) => !v)} data-testid="admin-campaign-toggle-skipped">
                    {showSkipped ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />}
                    {showSkipped ? "Skjul" : "Vis"} organisasjoner uten e-post ({preview.skipped.length})
                  </Button>
                  {showSkipped && (
                    <ul className="mt-1 max-h-28 overflow-y-auto rounded-md border border-border bg-background px-3 py-2 text-xs space-y-0.5" data-testid="admin-campaign-skipped-list">
                      {preview.skipped.map((s) => <li key={s.org_id} className="truncate">{s.org_name}</li>)}
                    </ul>
                  )}
                </div>
              )}
            </section>

            {/* Mal */}
            <section className="space-y-3" data-testid="admin-campaign-template">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label htmlFor="campaign-subject">Standardmal</Label>
                <Tabs value={lang} onValueChange={setLang}>
                  <TabsList className="h-8" data-testid="admin-campaign-lang-tabs">
                    {(template.langs || ["nn", "nb"]).map((l) => (
                      <TabsTrigger key={l} value={l} className="h-6 px-2.5 text-xs" data-testid={`admin-campaign-lang-${l}`}>{EMAIL_LANG_LABELS[l] || l}</TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                {template.image_url && (
                  <a href={template.image_url} target="_blank" rel="noreferrer" className="shrink-0 w-44 mx-auto sm:mx-0 sm:w-40" title="Bildet som ligger øverst i e-posten" data-testid="admin-campaign-image-link">
                    <img src={template.image_url} alt={template.image_alt || "Right in"} className="w-full rounded-lg border border-border" data-testid="admin-campaign-image" />
                  </a>
                )}
                <div className="flex-1 space-y-2 min-w-0">
                  <Input id="campaign-subject" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} placeholder="Emne" data-testid="admin-campaign-subject" />
                  <Textarea rows={9} value={body} onChange={(e) => setBody(e.target.value)} maxLength={8000} className="font-sans text-sm" data-testid="admin-campaign-body" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground" data-testid="admin-campaign-hint">
                Plassholdere: <code className="px-1 rounded bg-secondary">{"{organisasjon}"}</code> = organisasjonens navn, <code className="px-1 rounded bg-secondary">{"{navn}"}</code> = kontaktpersonens navn («Hei {"{navn}"}!» blir «Hei!» når e-posten går til organisasjonens adresse),
                {" "}<code className="px-1 rounded bg-secondary">{"{nyhetsbrev}"}</code> = mottakerens egen abonnér-lenke. Knapper skrives som <code className="px-1 rounded bg-secondary">{"[Knappetekst](lenke)"}</code> på egen linje (små, i bildets farger). Bildet øverst legges til automatisk – og «{template.cta}» + abonnér-lenken nederst hvis teksten mangler knapper.
              </p>
            </section>

            {/* Tidspunkt */}
            <section className="space-y-3" data-testid="admin-campaign-when">
              <Label>Når skal den sendes?</Label>
              <RadioGroup value={when} onValueChange={setWhen} className="grid sm:grid-cols-2 gap-2">
                <label className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors duration-150 ${when === "schedule" ? "border-brand bg-brand-soft/50" : "border-border hover:bg-secondary/60"}`}>
                  <RadioGroupItem value="schedule" className="mt-0.5" data-testid="admin-campaign-when-schedule" />
                  <span className="text-sm">
                    <span className="font-medium flex items-center gap-1.5"><CalendarClock className="w-4 h-4" /> Planlegg</span>
                    <span className="block text-xs text-muted-foreground">Velg dato og klokkeslett (norsk tid). Kan avbrytes frem til den starter.</span>
                  </span>
                </label>
                <label className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors duration-150 ${when === "now" ? "border-brand bg-brand-soft/50" : "border-border hover:bg-secondary/60"}`}>
                  <RadioGroupItem value="now" className="mt-0.5" data-testid="admin-campaign-when-now" />
                  <span className="text-sm">
                    <span className="font-medium flex items-center gap-1.5"><Zap className="w-4 h-4" /> Send nå</span>
                    <span className="block text-xs text-muted-foreground">Starter umiddelbart i bakgrunnen.</span>
                  </span>
                </label>
              </RadioGroup>
              {when === "schedule" && (
                <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="campaign-date" className="text-xs">Dato</Label>
                    <Input id="campaign-date" type="date" value={date} min={osloDateStr(new Date())} onChange={(e) => setDate(e.target.value)} className="sm:w-44" data-testid="admin-campaign-date" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="campaign-time" className="text-xs">Klokkeslett</Label>
                    <Input id="campaign-time" type="time" value={time} step={300} onChange={(e) => setTime(e.target.value)} className="sm:w-32" data-testid="admin-campaign-time" />
                  </div>
                  <p className={`text-sm pb-2 ${scheduleInvalid ? "text-[hsl(var(--danger))]" : "text-muted-foreground"}`} data-testid="admin-campaign-when-summary">
                    {!scheduledIso ? "Velg dato og klokkeslett." : scheduledInPast ? "Tidspunktet er allerede passert." : <>Sendes <strong className="text-foreground">{fmtOslo(scheduledIso)}</strong> norsk tid</>}
                  </p>
                </div>
              )}
            </section>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch checked={setStatus} onCheckedChange={setSetStatus} data-testid="admin-campaign-set-status" /> Sett «Ikke kontaktet» til «Kontaktet»
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch checked={ccMe} onCheckedChange={setCcMe} data-testid="admin-campaign-cc-me" /> Send meg rapport når den er ferdig
              </label>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 border-t border-border pt-4">
              <Button variant="outline" onClick={() => setPreviewOpen(true)} disabled={!preview || !subject.trim() || !body.trim()} className="sm:mr-auto" data-testid="admin-campaign-preview-button">
                <Eye className="w-4 h-4 mr-1.5" /> Forhåndsvis e-posten
              </Button>
              <Button variant="ghost" onClick={onClose} disabled={submitting} data-testid="admin-campaign-cancel">Avbryt</Button>
              <Button onClick={submit} disabled={!canSubmit} data-testid="admin-campaign-submit">
                {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : when === "schedule" ? <CalendarClock className="w-4 h-4 mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
                {when === "schedule" ? "Planlegg utsending" : "Send nå"} til {recipientsCount} mottaker{recipientsCount === 1 ? "" : "e"}
              </Button>
            </div>
            <EmailPreviewDialog
              open={previewOpen}
              onClose={() => setPreviewOpen(false)}
              subject={subject}
              body={body}
              lang={lang}
              recipients={preview?.recipients || []}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Viser den ferdige e-posten (HTML) slik én bestemt mottaker ser den – og sender en test til admins egen adresse. */
function EmailPreviewDialog({ open, onClose, subject, body, lang, orgId, recipients }) {
  const [sampleEmail, setSampleEmail] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const options = recipients || [];
  const sample = options.find((r) => r.email === sampleEmail) || options[0] || null;

  useEffect(() => {
    if (!open) return;
    setSampleEmail((options.find((r) => r.name) || options[0])?.email || "");
    setData(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    api.post("/admin/prospects/campaigns/render", {
      subject, body, lang, org_id: sample?.org_id || orgId || null, email: sample?.email || null, name: sample?.name || "",
    })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) { toast.error(errorMessage(err)); onClose(); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, subject, body, lang, orgId, sample?.email, sample?.org_id, sample?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const res = await api.post("/admin/prospects/campaigns/test-send", {
        subject, body, lang, org_id: sample?.org_id || orgId || null, email: sample?.email || null, name: sample?.name || "",
      });
      toast.success(`Test-e-post sendt til ${res.data.sent_to} – sjekk innboksen (og søppelpost)`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="admin-email-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5 text-brand" /> Forhåndsvisning av e-posten</DialogTitle>
          <DialogDescription>
            Slik ser e-posten ut hos mottakeren – med bilde, personlig hilsen, knapp og egen abonnér-lenke. «Send test-e-post til meg» sender akkurat denne versjonen til din egen adresse uten å røre organisasjonen.
          </DialogDescription>
        </DialogHeader>
        {options.length > 1 && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <Label className="shrink-0">Vis som mottaker</Label>
            <Select value={sample?.email || ""} onValueChange={setSampleEmail}>
              <SelectTrigger className="bg-background" data-testid="admin-email-preview-sample"><SelectValue /></SelectTrigger>
              <SelectContent>
                {options.slice(0, 100).map((r) => (
                  <SelectItem key={r.email} value={r.email}>
                    {r.org_name ? `${r.org_name} · ` : ""}{r.name || "organisasjonens adresse"} ({r.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {loading || !data ? (
          <div className="space-y-3" data-testid="admin-email-preview-loading"><Skeleton className="h-24 w-full" /><Skeleton className="h-[420px] w-full" /></div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-xl border border-border bg-secondary/40 p-3 text-sm space-y-1" data-testid="admin-email-preview-headers">
              <p><span className="inline-block w-16 text-muted-foreground">Fra</span> {data.sender}</p>
              <p className="break-all"><span className="inline-block w-16 text-muted-foreground">Til</span> {data.name ? `${data.name} <${data.to}>` : data.to}</p>
              <p className="break-all flex flex-wrap items-center gap-1.5">
                <span className="inline-block w-16 text-muted-foreground">Svar til</span> <span data-testid="admin-email-preview-reply-to">{data.reply_to}</span>
                {data.reply_tracking && <Badge variant="outline" className="text-[10px] border-brand/30 bg-brand-soft text-brand"><MessageSquareReply className="w-3 h-3 mr-1" /> Svar-sporing</Badge>}
              </p>
              <p><span className="inline-block w-16 text-muted-foreground">Emne</span> <strong data-testid="admin-email-preview-subject">{data.subject}</strong></p>
            </div>
            <iframe
              title="Forhåndsvisning av e-posten"
              srcDoc={data.html}
              sandbox="allow-same-origin"
              className="w-full h-[520px] rounded-xl border border-border bg-white"
              data-testid="admin-email-preview-frame"
            />
          </div>
        )}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="admin-email-preview-close">Lukk</Button>
          <Button variant="secondary" onClick={sendTest} disabled={sendingTest || !data} data-testid="admin-email-preview-send-test">
            {sendingTest ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Send test-e-post til meg
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Oversikt over planlagte, pågående og ferdige utsendinger – med avbryt og feilliste. */
function CampaignsDialog({ open, onClose, onChanged, onFinished, initialSetupOpen = false, onRedo }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null); // campaign id med åpen detalj
  const [toCancel, setToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [redoing, setRedoing] = useState(null); // campaign id som hentes for «Lag på nytt»

  const redo = async (c) => {
    setRedoing(c.id);
    try {
      const res = await api.get(`/admin/prospects/campaigns/${c.id}/redo`);
      if (res.data.orgs.length === 0) {
        toast.info(`Ingen igjen å sende til fra «${c.subject}» – alle har svart, er registrert eller er tatt ut`);
      }
      onRedo?.(res.data);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setRedoing(null);
    }
  };
  const activeIdsRef = useRef(new Set()); // utsendinger som pågikk/var planlagt sist vi så – ble noen ferdige, oppdateres listen bak
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  const load = useCallback(() => {
    api.get("/admin/prospects/campaigns")
      .then((res) => {
        setData(res.data);
        const nowActive = new Set(res.data.items.filter((c) => c.status === "scheduled" || c.status === "sending").map((c) => c.id));
        const finished = [...activeIdsRef.current].some((id) => !nowActive.has(id) && res.data.items.some((c) => c.id === id && (c.status === "sent" || c.status === "failed")));
        activeIdsRef.current = nowActive;
        if (finished && onFinishedRef.current) onFinishedRef.current();
      })
      .catch((err) => { toast.error(errorMessage(err)); setData({ items: [], counts: {} }); });
  }, []);

  useEffect(() => {
    if (!open) return;
    setData(null);
    setExpanded(null);
    activeIdsRef.current = new Set();
    load();
  }, [open, load]);

  // Oppdater automatisk mens noe er planlagt eller pågår
  const active = (data?.items || []).some((c) => c.status === "scheduled" || c.status === "sending");
  useEffect(() => {
    if (!open || !active) return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [open, active, load]);

  const cancel = async () => {
    if (!toCancel) return;
    setCancelling(true);
    try {
      const res = await api.delete(`/admin/prospects/campaigns/${toCancel.id}`);
      setData((prev) => {
        if (!prev) return prev;
        const items = prev.items.map((c) => (c.id === res.data.id ? res.data : c));
        const counts = {};
        items.forEach((c) => { counts[c.status] = (counts[c.status] || 0) + 1; });
        return { ...prev, items, counts };
      });
      toast.success(toCancel.status === "sending" ? `Utsendingen er stoppet – ${res.data.sent} var allerede sendt` : "Den planlagte utsendingen er avbrutt");
      setToCancel(null);
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setCancelling(false);
    }
  };

  const counts = data?.counts || {};
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="admin-campaigns-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarClock className="w-5 h-5 text-brand" /> Utsendinger</DialogTitle>
          <DialogDescription>
            Planlagte utsendinger sendes automatisk på valgt tidspunkt (norsk tid), én e-post per sekund, og fortsetter der de slapp hvis serveren starter på nytt.
            {counts.scheduled ? <> <strong>{counts.scheduled}</strong> planlagt</> : null}{counts.sending ? <> · <strong>{counts.sending}</strong> pågår</> : null}{counts.sent ? <> · {counts.sent} sendt</> : null}
          </DialogDescription>
        </DialogHeader>
        {data?.quota?.enabled && (
          <div className="rounded-lg border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="admin-campaigns-quota">
            <Clock className="w-3.5 h-3.5 text-brand shrink-0" />
            <span>Dagsgrense <strong className="text-foreground">{data.quota.limit}</strong> e-poster per døgn (samlet)</span>
            {data.quota.window_end ? (
              <span>· i dette døgnet <strong className="text-foreground">{data.quota.sent}</strong> sendt, {data.quota.remaining} igjen · nytt døgn {data.quota.window_end_label}</span>
            ) : (
              <span>· ingen sendt i inneværende døgn</span>
            )}
            {data.quota.exhausted && <span className="text-foreground">· utsendinger står på pause til {data.quota.window_end_label}</span>}
          </div>
        )}
        <ReplyTrackingPanel open={open} onChanged={onChanged} initialSetupOpen={initialSetupOpen} />
        {data === null ? (
          <div className="space-y-2" data-testid="admin-campaigns-loading"><Skeleton className="h-16 w-full" /><Skeleton className="h-16 w-full" /></div>
        ) : data.items.length === 0 ? (
          <p className="text-sm text-muted-foreground italic py-4" data-testid="admin-campaigns-empty">
            Ingen utsendinger ennå. Trykk «Velg flere» i listen, kryss av organisasjonene og velg «Send e-post til valgte».
          </p>
        ) : (
          <ul className="space-y-2" data-testid="admin-campaigns-list">
            {data.items.map((c) => {
              const Icon = CAMPAIGN_STATUS_ICONS[c.status] || Clock;
              const total = c.total || c.recipients_count || 0;
              const done = c.done || 0;
              const isOpen = expanded === c.id;
              return (
                <li key={c.id} className="rounded-xl border border-border bg-card p-3 space-y-2" data-testid={`admin-campaign-item-${c.id}`} data-status={c.status}>
                  <div className="flex flex-wrap items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium flex flex-wrap items-center gap-1.5">
                        <span className="truncate" data-testid={`admin-campaign-subject-${c.id}`}>{c.subject}</span>
                        <Badge variant="outline" className={`text-[10px] font-normal ${c.quota_paused ? "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 text-foreground" : (CAMPAIGN_STATUS_STYLES[c.status] || "")}`} data-testid={`admin-campaign-status-${c.id}`} data-quota-paused={c.quota_paused ? "true" : "false"}>
                          {c.quota_paused ? <Clock className="w-3 h-3 mr-1" /> : <Icon className={`w-3 h-3 mr-1 ${c.status === "sending" ? "animate-spin" : ""}`} />} {c.status_label}
                        </Badge>
                        <Badge variant="secondary" className="text-[10px] font-normal">{EMAIL_LANG_LABELS[c.lang] || c.lang}</Badge>
                        {c.target_label && (
                          <Badge variant="outline" className="text-[10px] font-normal" data-testid={`admin-campaign-target-${c.id}`}>
                            <MapPin className="w-3 h-3 mr-1" /> {c.target_label}
                          </Badge>
                        )}
                        {c.replies > 0 && (
                          <Badge variant="outline" className="text-[10px] font-normal border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" data-testid={`admin-campaign-replies-${c.id}`}>
                            <MessageSquareReply className="w-3 h-3 mr-1" /> {c.replies} svar
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5" data-testid={`admin-campaign-meta-${c.id}`}>
                        <span>{c.status === "scheduled" ? (c.quota_paused ? "Fortsetter" : "Sendes") : "Tidspunkt"} {fmtOslo(c.scheduled_at)}</span>
                        <span>· {c.recipients_count} mottaker{c.recipients_count === 1 ? "" : "e"}{c.skipped_count ? ` (${c.skipped_count} uten e-post)` : ""}</span>
                        {(c.status !== "scheduled" || c.quota_paused) && <span>· {c.sent} sendt{c.failed ? `, ${c.failed} feilet` : ""}{c.quota_paused ? `, ${Math.max(0, total - done)} gjenstår` : ""}</span>}
                        {c.created_by_name && <span>· av {c.created_by_name}</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {(c.failures?.length > 0 || c.recipients_count > 0) && (
                        <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => setExpanded(isOpen ? null : c.id)} data-testid={`admin-campaign-toggle-${c.id}`}>
                          {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                      )}
                      {(c.status === "scheduled" || c.status === "sending") && (
                        <Button variant="outline" size="sm" className="h-8 text-xs text-[hsl(var(--danger))] border-[hsl(var(--danger))]/40 hover:bg-[hsl(var(--danger))]/10 hover:text-[hsl(var(--danger))]" onClick={() => setToCancel(c)} data-testid={`admin-campaign-cancel-${c.id}`}>
                          <Ban className="w-3.5 h-3.5 mr-1" /> {c.status === "sending" ? "Stopp" : "Avbryt"}
                        </Button>
                      )}
                      {(c.status === "cancelled" || c.status === "sent" || c.status === "failed") && onRedo && (
                        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => redo(c)} disabled={redoing === c.id} title="Lag en ny utsending til de samme organisasjonene – de som har svart, er registrert eller er merket «ikke aktuell» tas ut" data-testid={`admin-campaign-redo-${c.id}`}>
                          {redoing === c.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />} Lag på nytt
                        </Button>
                      )}
                    </div>
                  </div>
                  {c.status === "sending" && total > 0 && (
                    <div className="space-y-1" data-testid={`admin-campaign-progress-${c.id}`}>
                      <Progress value={Math.round((done / total) * 100)} className="h-2" />
                      <p className="text-xs text-muted-foreground">{done} av {total} sendt</p>
                    </div>
                  )}
                  {isOpen && (
                    <div className="rounded-md border border-border bg-secondary/40 p-3 text-xs space-y-2" data-testid={`admin-campaign-detail-${c.id}`}>
                      <p className="text-muted-foreground whitespace-pre-line line-clamp-6">{c.body}</p>
                      {c.failures?.length > 0 && (
                        <div>
                          <p className="font-medium text-[hsl(var(--danger))] mb-1">Feilet ({c.failures.length}):</p>
                          <ul className="space-y-0.5">
                            {c.failures.map((f) => <li key={f.email} className="truncate">{f.org_name} &lt;{f.email}&gt; – {f.error}</li>)}
                          </ul>
                        </div>
                      )}
                      {c.recipients?.length > 0 && (
                        <div>
                          <p className="font-medium mb-1">Mottakere ({c.recipients.length}):</p>
                          <ul className="max-h-32 overflow-y-auto space-y-0.5">
                            {c.recipients.slice(0, 200).map((r) => <li key={r.email} className="truncate">{r.org_name}{r.name ? ` · ${r.name}` : ""} – {r.email}</li>)}
                          </ul>
                        </div>
                      )}
                      {c.cancelled_by && <p className="text-muted-foreground">Avbrutt av {c.cancelled_by}{c.finished_at ? ` ${fmtOslo(c.finished_at)}` : ""}</p>}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <AlertDialog open={!!toCancel} onOpenChange={(o) => !o && !cancelling && setToCancel(null)}>
          <AlertDialogContent data-testid="admin-campaign-cancel-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{toCancel?.status === "sending" ? "Stoppe utsendingen som pågår?" : "Avbryte den planlagte utsendingen?"}</AlertDialogTitle>
              <AlertDialogDescription>
                «{toCancel?.subject}» {toCancel?.status === "sending"
                  ? `stoppes – e-poster som allerede er sendt (${toCancel?.sent ?? 0}) kan ikke trekkes tilbake, resten sendes ikke.`
                  : `til ${toCancel?.recipients_count ?? 0} mottakere sendes ikke. Du kan lage en ny utsending når som helst.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={cancelling} data-testid="admin-campaign-cancel-no">Behold</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); cancel(); }}
                disabled={cancelling}
                className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90"
                data-testid="admin-campaign-cancel-yes"
              >
                {cancelling ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Ban className="w-4 h-4 mr-1.5" />} {toCancel?.status === "sending" ? "Stopp" : "Avbryt utsendingen"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}


/** Svar-sporing (Brevo inbound): status, oppsettsveiledning, webhook-opprettelse og de siste svarene. */
function ReplyTrackingPanel({ open, onChanged, initialSetupOpen = false }) {
  const [setup, setSetup] = useState(null);
  const [replies, setReplies] = useState(null);
  const [showSetup, setShowSetup] = useState(initialSetupOpen);
  const [showReplies, setShowReplies] = useState(false);
  const [creating, setCreating] = useState(false);
  const [working, setWorking] = useState("");

  const load = useCallback(() => {
    api.get("/admin/prospects/replies/setup").then((res) => setSetup(res.data)).catch((err) => { toast.error(errorMessage(err)); setSetup({ enabled: false, error: true }); });
    api.get("/admin/prospects/replies", { params: { limit: 15 } }).then((res) => setReplies(res.data)).catch(() => setReplies({ items: [], counts: {} }));
  }, []);

  useEffect(() => {
    if (!open) return;
    setSetup(null);
    setReplies(null);
    setShowSetup(initialSetupOpen);
    setShowReplies(false);
    load();
  }, [open, load, initialSetupOpen]);

  const createWebhook = async () => {
    setCreating(true);
    try {
      const res = await api.post("/admin/prospects/replies/setup/webhook");
      toast.success(res.data.action === "created" ? "Webhook opprettet hos Brevo" : res.data.action === "updated" ? "Webhook hos Brevo oppdatert" : "Webhooken hos Brevo er allerede riktig");
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} kopiert`);
    } catch {
      toast.error("Kunne ikke kopiere – merk teksten og kopier manuelt");
    }
  };

  const removeReply = async (r) => {
    setWorking(r.id);
    try {
      await api.delete(`/admin/prospects/replies/${r.id}`);
      setReplies((prev) => (prev ? { ...prev, items: prev.items.filter((x) => x.id !== r.id) } : prev));
      toast.success("Svaret er slettet");
      onChanged && onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setWorking("");
    }
  };

  if (setup === null) return <Skeleton className="h-14 w-full" data-testid="admin-reply-tracking-loading" />;
  const dnsOk = !!setup.dns?.ok;
  const hookOk = !!setup.brevo_webhook?.exists && !!setup.brevo_webhook?.url_matches;
  const allGood = setup.enabled && dnsOk && hookOk;
  const StatusBadge = ({ ok, unknown, okText, badText, unknownText }) => (
    <Badge variant="outline" className={`text-[10px] font-normal ${unknown ? "bg-muted text-muted-foreground border-border" : ok ? CAMPAIGN_STATUS_STYLES.sent : CAMPAIGN_STATUS_STYLES.failed}`}>
      {unknown ? unknownText : ok ? okText : badText}
    </Badge>
  );

  return (
    <div className={`rounded-xl border p-3 space-y-2 text-sm ${setup.enabled ? "border-brand/30 bg-brand-soft/30" : "border-border bg-secondary/40"}`} data-testid="admin-reply-tracking">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <p className="font-heading font-semibold flex flex-wrap items-center gap-2">
            <MessageSquareReply className="w-4 h-4 text-brand" /> Svar-sporing
            {setup.enabled ? (
              <Badge className={`text-[10px] ${allGood ? "bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]" : "bg-[hsl(var(--warning))] text-[hsl(var(--warning-foreground))] hover:bg-[hsl(var(--warning))]"}`} data-testid="admin-reply-tracking-status">
                {allGood ? "Aktiv" : "Slått på – oppsett mangler"}
              </Badge>
            ) : (
              <Badge variant="secondary" className="text-[10px]" data-testid="admin-reply-tracking-status">Ikke slått på</Badge>
            )}
          </p>
          <p className="text-xs text-muted-foreground" data-testid="admin-reply-tracking-summary">
            {setup.enabled
              ? <>Svar på e-postene går til <code className="px-1 rounded bg-secondary">{setup.example_address.replace("a1b2c3d4e5", "…")}</code>, organisasjonen markeres automatisk «Interessert» (bare fra «Ikke kontaktet»/«Kontaktet» – fraværsmeldinger teller ikke) og svaret videresendes til den som sendte e-posten.</>
              : <>Svar går i dag rett til din innboks uten at registeret merker det. Slå på svar-sporing i produksjon for å få organisasjoner automatisk satt til «Interessert» når de svarer.</>}
            {setup.replies_total > 0 && <> · <strong className="text-foreground">{setup.replies_total}</strong> svar mottatt{setup.replies_unmatched ? ` (${setup.replies_unmatched} uten treff)` : ""}</>}
          </p>
          {setup.enabled && (
            <div className="flex flex-wrap gap-1.5" data-testid="admin-reply-tracking-checks">
              <StatusBadge ok={dnsOk} okText="MX-post OK" badText={`MX-post mangler${setup.dns?.error ? "" : ""}`} />
              <StatusBadge ok={hookOk} unknown={!setup.brevo_webhook?.checked} okText="Brevo-webhook OK" badText="Brevo-webhook ikke opprettet" unknownText="Brevo-webhook: ukjent (mangler API-nøkkel)" />
              <StatusBadge ok={setup.secret_set} okText="Webhook-nøkkel satt" badText="Webhook-nøkkel mangler" />
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5 shrink-0">
          {replies && replies.items?.length > 0 && (
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setShowReplies((v) => !v)} data-testid="admin-reply-tracking-toggle-replies">
              <Inbox className="w-3.5 h-3.5 mr-1" /> Siste svar ({replies.counts?.total ?? replies.items.length})
            </Button>
          )}
          <Button asChild variant="outline" size="sm" className="h-8 text-xs" data-testid="admin-reply-tracking-open-inbox">
            <Link to="/dashboard/svar"><MessageSquareReply className="w-3.5 h-3.5 mr-1" /> Svar-innboks</Link>
          </Button>
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => setShowSetup((v) => !v)} data-testid="admin-reply-tracking-toggle-setup">
            {showSetup ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />} Oppsett
          </Button>
        </div>
      </div>

      {showSetup && (
        <ol className="space-y-3 rounded-lg border border-border bg-background p-3 text-xs list-decimal pl-7" data-testid="admin-reply-tracking-setup">
          <li className="space-y-1">
            <p className="font-medium text-sm">Legg inn MX-poster for <code className="px-1 rounded bg-secondary">{setup.domain || "svar.rightin.no"}</code> hos domeneleverandøren</p>
            <ul className="space-y-0.5 font-mono">
              {setup.mx_expected.map((m) => <li key={m.host}>{setup.domain || "svar.rightin.no"} &nbsp; MX &nbsp; {m.priority} &nbsp; {m.host}</li>)}
            </ul>
            <p className="text-muted-foreground">
              {setup.enabled
                ? (dnsOk ? "DNS-sjekk: OK – begge Brevo-tjenerne er funnet." : `DNS-sjekk: ${setup.dns?.error || "ikke funnet"}${setup.dns?.records?.length ? ` (funnet: ${setup.dns.records.map((r) => r.host).join(", ")})` : ""}. Endringer kan ta opptil noen timer.`)
                : "DNS sjekkes automatisk når INBOUND_REPLY_DOMAIN er satt."}
            </p>
            <p className="text-muted-foreground" data-testid="admin-reply-tracking-brevo-domain-hint">
              Legg også domenet til i Brevo: Settings → Senders, Domains, IPs → <strong>Domains</strong> → «Add a domain» → <code className="px-1 rounded bg-secondary">{setup.domain || "svar.rightin.no"}</code> → Authenticate.
              Brevo godtar ikke webhooken før domenet står som «Authenticated» der.
            </p>
          </li>
          <li className="space-y-1">
            <p className="font-medium text-sm">Legg inn tre hemmeligheter i Emergent (produksjon) og deploy på nytt</p>
            <ul className="space-y-0.5 font-mono">
              <li>INBOUND_REPLY_DOMAIN = {setup.domain || "svar.rightin.no"} {setup.enabled && <Check className="inline w-3 h-3 text-[hsl(var(--success))]" />}</li>
              <li>INBOUND_WEBHOOK_SECRET = (lang tilfeldig streng) {setup.secret_set && <Check className="inline w-3 h-3 text-[hsl(var(--success))]" />}</li>
              <li>BREVO_API_KEY = (v3-nøkkel: Brevo → Settings → SMTP &amp; API → API Keys) {setup.api_key_set && <Check className="inline w-3 h-3 text-[hsl(var(--success))]" />}</li>
            </ul>
            {setup.api_key?.set && (
              <p className={setup.api_key.kind === "api" ? "text-muted-foreground" : "text-[hsl(var(--warning))] font-medium"} data-testid="admin-reply-tracking-api-key-hint">
                Nøkkel i bruk på serveren: <code className="px-1 rounded bg-secondary">{setup.api_key.prefix}…{setup.api_key.suffix}</code> ({setup.api_key.length} tegn).
                {setup.api_key.kind === "api" && " Sammenlign de siste tegnene med listen i Brevo (SMTP & API → API keys) – stemmer de ikke, er en annen verdi lagt inn i Secrets, eller det er ikke redeployet etter endringen."}
                {setup.api_key.kind === "smtp" && " Dette er en SMTP-nøkkel (xsmtpsib-). Webhooken krever en API-nøkkel som begynner med xkeysib-."}
                {setup.api_key.kind === "unknown" && " Dette ser ikke ut som en Brevo API-nøkkel (skal begynne med xkeysib-)."}
                {setup.api_key.fixed?.length ? ` Ryddet automatisk: ${setup.api_key.fixed.join(", ")}.` : ""}
              </p>
            )}
            <p className="text-muted-foreground">API-nøkkelen brukes til å opprette webhooken og hente vedlegg – SMTP-brukernavn/-passord er ikke nok.</p>
          </li>
          <li className="space-y-1">
            <p className="font-medium text-sm">Registrer webhooken hos Brevo</p>
            {setup.webhook_url ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <code className="px-1.5 py-0.5 rounded bg-secondary break-all" data-testid="admin-reply-tracking-webhook-url">{setup.webhook_url.replace(/secret=.*/, "secret=••••••")}</code>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => copy(setup.webhook_url, "Webhook-URL")} data-testid="admin-reply-tracking-copy-url"><Copy className="w-3 h-3 mr-1" /> Kopier URL</Button>
                {setup.api_key_set && setup.enabled && (
                  <Button size="sm" className="h-7 text-xs" onClick={createWebhook} disabled={creating || hookOk} data-testid="admin-reply-tracking-create-webhook">
                    {creating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Radio className="w-3 h-3 mr-1" />} {hookOk ? "Webhook er registrert" : "Opprett webhook hos Brevo"}
                  </Button>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground">Webhook-URL vises når INBOUND_WEBHOOK_SECRET er satt.</p>
            )}
            <p className="text-muted-foreground">
              Type «inbound», hendelse «inboundEmailProcessed», domene {setup.domain || "svar.rightin.no"}.
              {setup.brevo_webhook?.checked && setup.brevo_webhook?.exists && !hookOk && " Brevo har en webhook for domenet, men den peker på en annen URL – «Opprett» oppdaterer den."}
              {setup.brevo_webhook?.error && !setup.api_key_set ? "" : setup.brevo_webhook?.error ? ` Brevo-sjekk: ${setup.brevo_webhook.error}` : ""}
            </p>
          </li>
          <li className="space-y-1">
            <p className="font-medium text-sm">Test</p>
            <p className="text-muted-foreground">Send en e-post fra din egen adresse til <code className="px-1 rounded bg-secondary">{setup.example_address}</code> (ukjent token → lagres som «uten treff» og videresendes til admin), eller svar på en test-e-post fra registeret.</p>
          </li>
        </ol>
      )}

      {showReplies && replies && (
        <ul className="divide-y divide-border rounded-lg border border-border bg-background" data-testid="admin-reply-tracking-replies">
          {replies.items.map((r) => (
            <li key={r.id} className="flex items-start gap-2 px-3 py-2 text-xs" data-testid={`admin-reply-item-${r.id}`}>
              <div className="min-w-0 flex-1 space-y-0.5">
                <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span className="tabular-nums text-muted-foreground">{formatDateTime(r.received_at)}</span>
                  <span className="font-medium break-all">{r.from_name ? `${r.from_name} <${r.from_email}>` : r.from_email}</span>
                  {r.org_name ? <span className="text-muted-foreground">→ {r.org_name}</span> : <Badge variant="outline" className={`text-[10px] font-normal ${CAMPAIGN_STATUS_STYLES.failed}`}>Uten treff</Badge>}
                  {r.kind !== "reply" && <Badge variant="secondary" className="text-[10px] font-normal">{r.kind_label}</Badge>}
                  {r.status_changed && <Badge variant="outline" className={`text-[10px] font-normal ${STATUS_STYLES.interessert}`}>Interessert</Badge>}
                </p>
                {r.subject && <p className="font-medium truncate">{r.subject}</p>}
                <p className="text-muted-foreground line-clamp-2 whitespace-pre-line">{r.excerpt}</p>
              </div>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-[hsl(var(--danger))]" onClick={() => removeReply(r)} disabled={working === r.id} title="Slett svaret" data-testid={`admin-reply-delete-${r.id}`}>
                {working === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


const EMPTY_NEW_ORG = {
  name: "", kind: "annet", kommunenummer: "", kommune: "", org_number: "", email: "", phone: "", website: "",
  adresse: "", postnummer: "", poststed: "", notes: "",
  contact: { name: "", role: "", email: "", phone: "" },
};

function NewOrgDialog({ open, onClose, kinds, kommuner, defaultKommune, onCreated }) {
  const [form, setForm] = useState(EMPTY_NEW_ORG);
  const [submitting, setSubmitting] = useState(false);
  const nameRef = useRef(null);

  useEffect(() => {
    if (open) {
      const kn = kommuner.some((k) => k.nummer === defaultKommune) ? defaultKommune : (kommuner[0]?.nummer || "annen");
      setForm({ ...EMPTY_NEW_ORG, kommunenummer: kn, contact: { ...EMPTY_NEW_ORG.contact } });
      setTimeout(() => nameRef.current?.focus(), 100);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setContact = (patch) => setForm((f) => ({ ...f, contact: { ...f.contact, ...patch } }));
  const orgDigits = form.org_number.replace(/\D/g, "");
  const orgNrInvalid = orgDigits.length > 0 && orgDigits.length !== 9;
  const kommuneMissing = form.kommunenummer === "annen" && !form.kommune.trim();
  const canSubmit = form.name.trim().length > 0 && !orgNrInvalid && !kommuneMissing && !submitting;

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const contact = form.contact;
      const res = await api.post("/admin/prospects", {
        name: form.name, kind: form.kind, org_number: orgDigits,
        kommunenummer: form.kommunenummer === "annen" ? "" : form.kommunenummer,
        kommune: form.kommunenummer === "annen" ? form.kommune : "",
        email: form.email, phone: form.phone, website: form.website,
        adresse: form.adresse, postnummer: form.postnummer, poststed: form.poststed, notes: form.notes,
        contacts: contact.name.trim() || contact.email.trim() ? [contact] : [],
      });
      toast.success(`«${res.data.name}» er lagt inn i kontaktregisteret`);
      onCreated(res.data);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="admin-prospects-new-org-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-brand" /> Legg inn organisasjon manuelt</DialogTitle>
          <DialogDescription>
            For organisasjoner som ikke fanges opp fra Brønnøysund – uformelle lag, festivalkomiteer, arrangører uten org.nr eller utenfor regionen.
            Manuelle organisasjoner deaktiveres ikke av synk, og det du fyller inn her overskrives ikke.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="new-org-name">Navn på organisasjonen <span className="text-[hsl(var(--danger))]">*</span></Label>
              <Input id="new-org-name" ref={nameRef} value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="F.eks. Husnes Kulturfestival" required data-testid="admin-new-org-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.kind} onValueChange={(v) => set({ kind: v })}>
                <SelectTrigger className="bg-background" data-testid="admin-new-org-kind"><SelectValue /></SelectTrigger>
                <SelectContent>{kinds.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Kommune</Label>
              <Select value={form.kommunenummer || "annen"} onValueChange={(v) => set({ kommunenummer: v })}>
                <SelectTrigger className="bg-background" data-testid="admin-new-org-kommune"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {kommuner.map((k) => <SelectItem key={k.nummer} value={k.nummer}>{k.navn}</SelectItem>)}
                  <SelectItem value="annen">Annen kommune (utenfor regionen)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.kommunenummer === "annen" && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="new-org-kommune">Kommunenavn <span className="text-[hsl(var(--danger))]">*</span></Label>
                <Input id="new-org-kommune" value={form.kommune} onChange={(e) => set({ kommune: e.target.value })} placeholder="F.eks. Bergen" data-testid="admin-new-org-kommune-navn" />
                <p className="text-xs text-muted-foreground">Organisasjoner utenfor regionen vises under fanen «Alle».</p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="new-org-nr">Org.nr <span className="text-muted-foreground font-normal">(valgfritt)</span></Label>
              <Input id="new-org-nr" inputMode="numeric" value={form.org_number} onChange={(e) => set({ org_number: e.target.value })} placeholder="9 siffer" aria-invalid={orgNrInvalid} className={orgNrInvalid ? "border-[hsl(var(--danger))]" : ""} data-testid="admin-new-org-orgnr" />
              {orgNrInvalid && <p className="text-xs text-[hsl(var(--danger))]" data-testid="admin-new-org-orgnr-error">Org.nr må ha 9 siffer.</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-org-email">E-post til organisasjonen</Label>
              <Input id="new-org-email" type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} placeholder="post@organisasjon.no" data-testid="admin-new-org-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-org-phone">Telefon</Label>
              <Input id="new-org-phone" value={form.phone} onChange={(e) => set({ phone: e.target.value })} data-testid="admin-new-org-phone" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-org-web">Nettside</Label>
              <Input id="new-org-web" value={form.website} onChange={(e) => set({ website: e.target.value })} placeholder="www.organisasjon.no" data-testid="admin-new-org-website" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="new-org-adresse">Adresse</Label>
              <Input id="new-org-adresse" value={form.adresse} onChange={(e) => set({ adresse: e.target.value })} data-testid="admin-new-org-adresse" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-org-postnr">Postnr</Label>
              <PostalCodeInput id="new-org-postnr" value={form.postnummer} onChange={(v) => set({ postnummer: v })} onPlace={(p) => set({ poststed: p.poststed })} limit={6} testId="admin-new-org-postnummer" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-org-poststed">Poststed</Label>
              <Input id="new-org-poststed" value={form.poststed} onChange={(e) => set({ poststed: e.target.value })} data-testid="admin-new-org-poststed" />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="new-org-notes">Notat</Label>
              <Textarea id="new-org-notes" rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Hvem tipset, hva de arrangerer …" data-testid="admin-new-org-notes" />
            </div>
          </div>

          <div className="rounded-xl border border-dashed border-border p-3 space-y-2" data-testid="admin-new-org-contact">
            <p className="text-sm font-medium flex items-center gap-2"><UserPlus className="w-4 h-4 text-brand" /> Kontaktperson <span className="text-muted-foreground font-normal">(valgfritt – flere kan legges til etterpå)</span></p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input value={form.contact.name} onChange={(e) => setContact({ name: e.target.value })} placeholder="Navn" data-testid="admin-new-org-contact-name" />
              <Input value={form.contact.role} onChange={(e) => setContact({ role: e.target.value })} placeholder="Rolle (f.eks. leder)" data-testid="admin-new-org-contact-role" />
              <Input value={form.contact.email} onChange={(e) => setContact({ email: e.target.value })} placeholder="E-post" type="email" data-testid="admin-new-org-contact-email" />
              <Input value={form.contact.phone} onChange={(e) => setContact({ phone: e.target.value })} placeholder="Telefon" data-testid="admin-new-org-contact-phone" />
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting} data-testid="admin-new-org-cancel">Avbryt</Button>
            <Button type="submit" disabled={!canSubmit} data-testid="admin-new-org-submit">
              {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />} Legg inn organisasjonen
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const SOURCE_TEXT = {
  own: "navnet på din konto",
  shared: "det felles avsendernavnet",
  env: "OUTREACH_SENDER_NAME (serverinnstilling)",
  default: "standardverdien «Right in» – legg inn et navn under",
};

/** Innstillinger for signaturen ({admin} i e-postmalen): eget navn på kontoen og felles avsendernavn for alle admin-brukere. */
function SignatureSettingsDialog({ open, onClose }) {
  const [data, setData] = useState(null);
  const [myName, setMyName] = useState("");
  const [sharedName, setSharedName] = useState("");
  const [dailyLimit, setDailyLimit] = useState("25");
  const [reportEnabled, setReportEnabled] = useState(true);
  const [reportHour, setReportHour] = useState("20");
  const [sendingReport, setSendingReport] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get("/admin/prospects/settings")
      .then((res) => { setData(res.data); setMyName(res.data.my_name || ""); setSharedName(res.data.shared_name || ""); setDailyLimit(String(res.data.daily_limit ?? 25)); setReportEnabled(res.data.daily_report_enabled !== false); setReportHour(String(res.data.daily_report_hour ?? 20)); })
      .catch((err) => { toast.error(errorMessage(err)); setData({ error: true }); });
  }, [open]);

  const limitNum = Math.max(0, Math.min(data?.daily_limit_max || 2000, parseInt(dailyLimit || "0", 10) || 0));
  const hourNum = Math.max(0, Math.min(23, parseInt(reportHour || "0", 10) || 0));
  const dirty = !!data && !data.error && (myName.trim() !== (data.my_name || "") || sharedName.trim() !== (data.shared_name || "") || limitNum !== (data.daily_limit ?? 25)
    || reportEnabled !== (data.daily_report_enabled !== false) || hourNum !== (data.daily_report_hour ?? 20));
  const previewName = myName.trim() || sharedName.trim() || (data?.env_name_set ? data.effective_name : "Right in");

  const sendReportNow = async () => {
    setSendingReport(true);
    try {
      const res = await api.post("/admin/prospects/settings/daily-report/send");
      const sm = res.data.summary || {};
      toast.success(`Dagsrapport sendt til ${res.data.to}: ${sm.sent_today ?? 0} sendt i dag, ${sm.remaining ?? 0} står igjen`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSendingReport(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/prospects/settings", { my_name: myName.trim(), shared_name: sharedName.trim(), daily_limit: limitNum, daily_report_enabled: reportEnabled, daily_report_hour: hourNum });
      setData(res.data);
      setMyName(res.data.my_name || "");
      setSharedName(res.data.shared_name || "");
      setDailyLimit(String(res.data.daily_limit ?? 25));
      setReportEnabled(res.data.daily_report_enabled !== false);
      setReportHour(String(res.data.daily_report_hour ?? 20));
      toast.success(res.data.daily_limit > 0 ? `Lagret – signatur «${res.data.effective_name}», maks ${res.data.daily_limit} e-poster per døgn` : `Lagret – signatur «${res.data.effective_name}», ingen dagsgrense`);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="admin-signature-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PenLine className="w-5 h-5 text-brand" /> Innstillinger for utsendinger</DialogTitle>
          <DialogDescription>
            Signaturnavnet som settes inn for <code className="px-1 rounded bg-secondary">{"{admin}"}</code> i standardmalen og i svar fra innboksen, og dagsgrensen for hvor mange e-poster som sendes per døgn.
          </DialogDescription>
        </DialogHeader>
        {data === null ? (
          <div className="space-y-3" data-testid="admin-signature-settings-loading"><Skeleton className="h-10 w-full" /><Skeleton className="h-10 w-full" /><Skeleton className="h-16 w-full" /></div>
        ) : data.error ? (
          <p className="text-sm text-muted-foreground" data-testid="admin-signature-settings-error">Kunne ikke hente innstillingene – lukk og prøv igjen.</p>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="signature-my-name">Ditt navn (denne kontoen)</Label>
              <Input id="signature-my-name" value={myName} onChange={(e) => setMyName(e.target.value.slice(0, data.max_len || 80))} placeholder="F.eks. Per Ove Kviteberg" data-testid="admin-signature-my-name-input" />
              <p className="text-xs text-muted-foreground">Brukes først. Vises også som kontaktperson på kontoen. Hver admin kan ha sitt eget navn.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="signature-shared-name">Felles avsendernavn (alle admin-brukere)</Label>
              <Input id="signature-shared-name" value={sharedName} onChange={(e) => setSharedName(e.target.value.slice(0, data.max_len || 80))} placeholder="Brukes når kontoen mangler navn" data-testid="admin-signature-shared-name-input" />
              <p className="text-xs text-muted-foreground">Reserve når en admin-konto ikke har eget navn. Tomt = «Right in».</p>
            </div>
            <div className="rounded-lg border border-border bg-secondary/40 p-3 text-sm" data-testid="admin-signature-preview">
              <p className="text-xs text-muted-foreground mb-1">Slik blir signaturen{dirty ? " (ikke lagret ennå)" : ""}:</p>
              <p className="whitespace-pre-line font-medium" data-testid="admin-signature-preview-text">{`Venleg helsing\n${previewName}\n${data.company}`}</p>
              {!dirty && <p className="text-xs text-muted-foreground mt-2">Hentes nå fra {SOURCE_TEXT[data.source] || data.source}.</p>}
            </div>
            <p className="text-xs text-muted-foreground">Gjelder nye utsendinger og svar. Allerede planlagte utsendinger beholder teksten de ble opprettet med.</p>
            <div className="space-y-1.5 border-t border-border pt-4" data-testid="admin-daily-limit-section">
              <Label htmlFor="daily-limit" className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-brand" /> Dagsgrense for utsendinger (e-poster per døgn)</Label>
              <div className="flex items-center gap-3">
                <Input id="daily-limit" type="number" inputMode="numeric" min={0} max={data.daily_limit_max || 2000} value={dailyLimit}
                  onChange={(e) => setDailyLimit(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))} className="w-28" data-testid="admin-daily-limit-input" />
                <span className="text-sm text-muted-foreground">{limitNum > 0 ? `maks ${limitNum} e-post${limitNum === 1 ? "" : "er"} per døgn, samlet for alle utsendinger` : "0 = ingen grense (alt sendes fortløpende, én e-post per sekund)"}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Døgnet regnes fra første e-post: når grensen er nådd, settes utsendingen på pause og fortsetter automatisk ved samme klokkeslett neste dag – til alle mottakerne har fått sin e-post. Standard: {data.daily_limit_default ?? 25}.
              </p>
              {data.quota?.enabled && (
                <p className="text-xs text-muted-foreground" data-testid="admin-daily-limit-status">
                  {data.quota.window_end
                    ? `I dette døgnet: ${data.quota.sent} av ${data.quota.limit} sendt · nytt døgn ${data.quota.window_end_label}`
                    : `Ingen e-poster sendt i inneværende døgn – første e-post starter et nytt døgn.`}
                </p>
              )}
            </div>
            <div className="space-y-2 border-t border-border pt-4" data-testid="admin-daily-report-section">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className="flex items-center gap-1.5"><BellRing className="w-4 h-4 text-brand" /> Dagsrapport om utsendinger</Label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <Switch checked={reportEnabled} onCheckedChange={setReportEnabled} data-testid="admin-daily-report-toggle" /> {reportEnabled ? "På" : "Av"}
                </label>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-muted-foreground">Sendes hver kveld kl.</span>
                <Select value={String(hourNum)} onValueChange={setReportHour} disabled={!reportEnabled}>
                  <SelectTrigger className="w-24 h-9" data-testid="admin-daily-report-hour"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                      <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">norsk tid</span>
                <Button variant="outline" size="sm" onClick={sendReportNow} disabled={sendingReport} className="ml-auto" data-testid="admin-daily-report-send-now">
                  {sendingReport ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Mail className="w-4 h-4 mr-1.5" />} Send meg rapporten nå
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Kort e-post til den som har laget utsendingene: hvor mange som fikk e-post i dag, hvor mange som feilet eller svarte, hvor mange som står igjen og når sendingen fortsetter. Dager uten utsendinger gir ingen rapport.
                {data.daily_report_last_sent_at ? <> Sist sendt {fmtOslo(data.daily_report_last_sent_at)}{data.daily_report_last_to?.length ? ` til ${data.daily_report_last_to.join(", ")}` : ""}.</> : null}
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose} data-testid="admin-signature-cancel-button">Lukk</Button>
              <Button onClick={save} disabled={saving || !dirty} data-testid="admin-signature-save-button">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />} Lagre
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
